import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Activity, BarChart3, Cpu, HeartPulse, Search, Shield, ShieldCheck,
  ChevronRight, ToggleLeft, ToggleRight, UserPlus, RefreshCw, Lock, LogOut,
  CheckCircle2, XCircle, Clock, Database, Zap, MessageSquare, KeyRound,
  Eye, FileText, AlertTriangle, Wrench, UserCog, ChevronDown, Copy, ExternalLink,
  Mail, Send, Trash2, Save, Newspaper, Upload, Brain, BookOpen, Lightbulb,
  Settings, Star, Target, Sparkles, Plus, CreditCard,
} from 'lucide-react';
import '../styles/AdminConsole.css';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/* ═══════════════════════════════════════════════════════════
   ADMIN LOGIN GATE
   ═══════════════════════════════════════════════════════════ */
function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login mislukt');
      onLogin(data.token, data.user);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="admin-login-page" data-testid="admin-login-page">
      <div className="admin-login-card">
        <div className="admin-login-icon"><ShieldCheck size={32} /></div>
        <h2>Admin Console</h2>
        <p className="admin-login-sub">Krypton9 Platform Management</p>
        {error && <div className="admin-login-error" data-testid="admin-login-error"><AlertTriangle size={14} /> {error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="admin-login-field">
            <Lock size={15} />
            <input data-testid="admin-email" type="email" placeholder="Admin email" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div className="admin-login-field">
            <KeyRound size={15} />
            <input data-testid="admin-password" type="password" placeholder="Wachtwoord" value={password} onChange={e => setPassword(e.target.value)} required />
          </div>
          <button type="submit" className="admin-login-btn" disabled={loading} data-testid="admin-login-submit">
            {loading ? <RefreshCw size={16} className="spin" /> : <><Shield size={16} /> Inloggen</>}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN ADMIN CONSOLE
   ═══════════════════════════════════════════════════════════ */
const TABS = [
  { id: 'users', label: 'Gebruikers', icon: Users },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'gateway', label: 'Context Gateway', icon: Shield },
  { id: 'ai', label: 'AI / Models', icon: Cpu },
  { id: 'emma-brain', label: 'Emma Brain', icon: Brain },
  { id: 'email', label: 'Email / Chat', icon: Mail },
  { id: 'newsletter', label: 'Newsletter', icon: Send },
  { id: 'system', label: 'Systeem', icon: HeartPulse },
];

export default function AdminConsole() {
  const [adminToken, setAdminToken] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [tab, setTab] = useState('users');

  const handleLogin = (token, user) => {
    setAdminToken(token);
    setAdminUser(user);
  };

  const handleLogout = () => {
    setAdminToken(null);
    setAdminUser(null);
  };

  if (!adminToken) return <AdminLogin onLogin={handleLogin} />;

  const headers = { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' };

  return (
    <div className="admin-console" data-testid="admin-console">
      <div className="admin-header">
        <div>
          <h1 className="admin-title" data-testid="admin-title"><Shield size={22} /> Admin Console</h1>
          <p className="admin-subtitle">Krypton9 Platform Management</p>
        </div>
        <div className="admin-header-right">
          <span className="admin-user-badge"><UserCog size={14} /> {adminUser?.full_name}</span>
          <button className="admin-btn-sm" onClick={handleLogout} data-testid="admin-logout"><LogOut size={14} /> Uitloggen</button>
        </div>
      </div>

      <nav className="admin-tabs" data-testid="admin-tabs">
        {(Array.isArray(TABS) ? TABS : []).map(t => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} className={`admin-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </nav>

      <div className="admin-content">
        {tab === 'users' && <UsersPanel headers={headers} adminToken={adminToken} />}
        {tab === 'analytics' && <AnalyticsPanel headers={headers} />}
        {tab === 'gateway' && <GatewayPanel headers={headers} />}
        {tab === 'ai' && <AIPanel headers={headers} />}
        {tab === 'emma-brain' && <EmmaBrainPanel headers={headers} />}
        {tab === 'email' && <EmailPanel headers={headers} />}
        {tab === 'newsletter' && <NewsletterPanel headers={headers} />}
        {tab === 'system' && <SystemPanel headers={headers} />}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   USERS PANEL
   ═══════════════════════════════════════════════════════════ */
function UsersPanel({ headers, adminToken }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, active, unverified, inactive
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchUsers = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/users?search=${encodeURIComponent(q)}&limit=50`, { headers });
      const data = await res.json();
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch { }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, []);

  const filteredUsers = (Array.isArray(users) ? users : []).filter(u => {
    if (filter === 'unverified') return !u.is_verified;
    if (filter === 'inactive') return !u.is_active;
    if (filter === 'active') return u.is_active && u.is_verified;
    return true;
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchDetail = async (uid) => {
    setSelected(uid);
    try {
      const res = await fetch(`${API}/admin/users/${uid}`, { headers });
      setDetail(await res.json());
    } catch { setDetail(null); }
  };

  const updateUser = async (uid, updates) => {
    await fetch(`${API}/admin/users/${uid}`, { method: 'PATCH', headers, body: JSON.stringify(updates) });
    fetchUsers(search);
    if (selected === uid) fetchDetail(uid);
  };

  const adminAction = async (uid, action, body = {}) => {
    const res = await fetch(`${API}/admin/users/${uid}/${action}`, { method: 'POST', headers, body: JSON.stringify(body) });
    const data = await res.json();
    if (selected === uid) fetchDetail(uid);
    return data;
  };

  const createDemo = async (formData) => {
    const res = await fetch(`${API}/admin/users/create-demo`, { method: 'POST', headers, body: JSON.stringify(formData) });
    const data = await res.json();
    if (data.created) { setShowCreate(false); fetchUsers(search); }
    return data;
  };

  const deleteUser = async (uid) => {
    const res = await fetch(`${API}/admin/users/${uid}`, { method: 'DELETE', headers });
    const data = await res.json();
    if (data.deleted) { setDeleteConfirm(null); setSelected(null); setDetail(null); fetchUsers(search); }
    return data;
  };

  return (
    <div className="admin-users-layout">
      <div className="admin-user-list">
        <div className="admin-search-bar">
          <Search size={16} />
          <input data-testid="admin-user-search" placeholder="Zoek op naam, email of bedrijf..." value={search}
            onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchUsers(search)} />
          <button className="admin-btn-sm" onClick={() => fetchUsers(search)}><Search size={13} /></button>
          <button className="admin-btn-sm accent" onClick={() => setShowCreate(true)} data-testid="create-demo-btn"><UserPlus size={14} /></button>
        </div>
        <div className="admin-filter-bar" data-testid="user-filter-bar">
          {[
            { id: 'all', label: 'Alle' },
            { id: 'active', label: 'Actief' },
            { id: 'unverified', label: 'Onverified' },
            { id: 'inactive', label: 'Inactief' },
          ].map(f => (
            <button key={f.id} className={`admin-filter-btn ${filter === f.id ? 'active' : ''}`}
              onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}>{f.label}</button>
          ))}
        </div>
        <div className="admin-user-count">{filteredUsers.length} van {total} gebruikers {loading && <RefreshCw size={11} className="spin" />}</div>
        <div className="admin-user-items">
          {(Array.isArray(filteredUsers) ? filteredUsers : []).map(u => (
            <div key={u.id} className={`admin-user-item ${selected === u.id ? 'selected' : ''}`} onClick={() => fetchDetail(u.id)} data-testid={`user-item-${u.id}`}>
              <div className="admin-user-item-main">
                <span className="admin-user-name">{u.full_name}</span>
                <span className="admin-user-email">{u.email}</span>
              </div>
              <div className="admin-user-badges">
                {u.role === 'admin' && <span className="badge badge-admin">admin</span>}
                {u.is_active ? <span className="badge badge-active">actief</span> : <span className="badge badge-inactive">inactief</span>}
                {!u.is_verified && <span className="badge badge-unverified">onverified</span>}
              </div>
              <ChevronRight size={14} className="admin-chevron" />
            </div>
          ))}
        </div>
      </div>

      <div className="admin-user-detail">
        {detail ? (
          <UserDetailView detail={detail} onUpdate={updateUser} onAction={adminAction} onDelete={deleteUser} />
        ) : (
          <div className="admin-empty-state"><Users size={40} strokeWidth={1} /><p>Selecteer een gebruiker</p></div>
        )}
      </div>

      {showCreate && <CreateDemoModal onClose={() => setShowCreate(false)} onCreate={createDemo} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   USER DETAIL VIEW
   ═══════════════════════════════════════════════════════════ */
function UserDetailView({ detail, onUpdate, onAction, onDelete }) {
  const u = detail.user;
  const [activeSection, setActiveSection] = useState('overview');
  const [newPw, setNewPw] = useState('');
  const [pwResult, setPwResult] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  if (!u) return null;

  const handleResetPw = async () => {
    const pw = newPw || 'Reset1234!';
    const res = await onAction(u.id, 'reset-password', { password: pw });
    if (res?.reset) setPwResult(`Wachtwoord gereset naar: ${res.password}`);
  };

  const handleImpersonate = async () => {
    const res = await onAction(u.id, 'impersonate');
    if (res?.token) {
      // Open new tab with impersonated session
      const url = `${window.location.origin}/impersonate?token=${encodeURIComponent(res.token)}&name=${encodeURIComponent(res.full_name || u.full_name)}`;
      window.open(url, '_blank');
    }
  };

  const sections = [
    { id: 'overview', label: 'Overzicht' },
    { id: 'tools', label: 'Tools' },
    { id: 'errors', label: 'Errors' },
    { id: 'actions', label: 'Acties' },
  ];

  return (
    <div className="admin-detail-content" data-testid="user-detail-view">
      <div className="admin-detail-header">
        <div>
          <h3>{u.full_name}</h3>
          <p className="admin-detail-email">{u.email}</p>
          <p className="admin-detail-company">{u.company_name}</p>
        </div>
        <div className="admin-user-badges">
          {u.role === 'admin' && <span className="badge badge-admin">ADMIN</span>}
          {u.is_active ? <span className="badge badge-active">ACTIEF</span> : <span className="badge badge-inactive">INACTIEF</span>}
        </div>
      </div>

      <div className="admin-detail-tabs">
        {(Array.isArray(sections) ? sections : []).map(s => (
          <button key={s.id} className={`admin-detail-tab ${activeSection === s.id ? 'active' : ''}`} onClick={() => setActiveSection(s.id)}>{s.label}</button>
        ))}
      </div>

      {activeSection === 'overview' && (
        <>
          <div className="admin-detail-grid">
            <div className="admin-detail-field">
              <label>Rol</label>
              <select value={u.role || 'user'} onChange={e => onUpdate(u.id, { role: e.target.value })} data-testid="user-role-select">
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="admin-detail-field">
              <label>Status</label>
              <button className={`admin-toggle ${u.is_active ? 'on' : 'off'}`} onClick={() => onUpdate(u.id, { is_active: !u.is_active })} data-testid="user-toggle-active">
                {u.is_active ? <><ToggleRight size={16} /> Actief</> : <><ToggleLeft size={16} /> Inactief</>}
              </button>
            </div>
            <div className="admin-detail-field">
              <label>Email verificatie</label>
              <button className={`admin-toggle ${u.is_verified ? 'on' : 'off'}`} onClick={() => onUpdate(u.id, { is_verified: !u.is_verified })} data-testid="user-toggle-verified">
                {u.is_verified ? <><CheckCircle2 size={14} /> Geverifieerd</> : <><XCircle size={14} /> Niet geverifieerd</>}
              </button>
            </div>
            <div className="admin-detail-field">
              <label>Credits vrijstelling</label>
              <button className={`admin-toggle ${u.credits_exempt ? 'on' : 'off'}`} onClick={() => onUpdate(u.id, { credits_exempt: !u.credits_exempt })} data-testid="user-toggle-credits-exempt">
                {u.credits_exempt ? <><ToggleRight size={16} /> Onbeperkt</> : <><ToggleLeft size={16} /> Normaal</>}
              </button>
            </div>
            <div className="admin-detail-field">
              <label>Aangemaakt</label>
              <span className="admin-detail-val">{new Date(u.created_at).toLocaleDateString('nl-NL', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>

          <div className="admin-detail-stats">
            <StatCard icon={Zap} num={detail.tool_usage_count || 0} label="Tool gebruik" />
            <StatCard icon={MessageSquare} num={detail.session_count || 0} label="Chat sessies" />
            <StatCard icon={FileText} num={detail.knowledge_count || 0} label="Documenten" />
          </div>

          {detail.last_tool && (
            <div className="admin-info-row"><Clock size={13} /> Laatste tool: <strong>{detail.last_tool.tool_name}</strong> — {new Date(detail.last_tool.timestamp).toLocaleString('nl-NL')}</div>
          )}
          {detail.last_session && (
            <div className="admin-info-row"><MessageSquare size={13} /> Laatste sessie: {new Date(detail.last_session.last_updated).toLocaleString('nl-NL')}</div>
          )}

          {detail.onboarding && (
            <div className="admin-detail-section">
              <h4>Onboarding</h4>
              <div className="admin-config-grid">
                <ConfigRow label="Status" value={detail.onboarding.completed ? 'Voltooid' : 'Niet voltooid'} status={detail.onboarding.completed ? 'ok' : 'warning'} />
                {detail.onboarding.role && <ConfigRow label="Rol" value={detail.onboarding.role} />}
                {detail.onboarding.industry && <ConfigRow label="Industrie" value={detail.onboarding.industry} />}
              </div>
            </div>
          )}

          {detail.llm_settings && (
            <div className="admin-detail-section">
              <h4>LLM Instellingen</h4>
              <div className="admin-config-grid">
                <ConfigRow label="Provider" value={detail.llm_settings.provider || 'standaard'} />
                <ConfigRow label="Model" value={detail.llm_settings.model || 'standaard'} />
              </div>
            </div>
          )}

          {(detail.recent_notifications || []).length > 0 && (
            <div className="admin-detail-section">
              <h4>Laatste Notificaties ({detail.recent_notifications.length})</h4>
              {(Array.isArray(detail.recent_notifications) ? detail.recent_notifications : []).map((n, i) => (
                <div key={i} className="admin-notif-item">
                  <span className="admin-notif-title">{n.title || n.message}</span>
                  <span className="admin-notif-time">{n.created_at ? new Date(n.created_at).toLocaleDateString('nl-NL') : ''}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeSection === 'tools' && (
        <div className="admin-detail-section">
          <h4>Tool Gebruik per Tool</h4>
          {(detail.tool_breakdown || []).length > 0 ? (
            <div className="admin-tool-bars">
              {(Array.isArray(detail.tool_breakdown) ? detail.tool_breakdown : []).map((t, i) => {
                const max = detail.tool_breakdown[0]?.count || 1;
                return (
                  <div key={i} className="admin-tool-bar-row">
                    <span className="admin-tool-name">{t.name}</span>
                    <div className="admin-tool-bar-track"><div className="admin-tool-bar-fill" style={{ width: `${(t.count / max) * 100}%` }} /></div>
                    <span className="admin-tool-count">{t.count}x</span>
                    {t.last_used && <span className="admin-tool-date">{new Date(t.last_used).toLocaleDateString('nl-NL')}</span>}
                  </div>
                );
              })}
            </div>
          ) : <p className="admin-muted">Geen tool gebruik gevonden</p>}
        </div>
      )}

      {activeSection === 'errors' && (
        <div className="admin-detail-section">
          <h4><AlertTriangle size={14} /> Laatste API Errors</h4>
          {(detail.api_errors || []).length > 0 ? (
            (Array.isArray(detail.api_errors) ? detail.api_errors : []).map((e, i) => (
              <div key={i} className="admin-error-item">
                <div className="admin-error-header">
                  <span className="badge badge-inactive">{e.status_code || 'ERR'}</span>
                  <span className="admin-error-path">{e.path || e.endpoint}</span>
                  <span className="admin-notif-time">{e.timestamp ? new Date(e.timestamp).toLocaleString('nl-NL') : ''}</span>
                </div>
                {e.message && <p className="admin-error-msg">{e.message}</p>}
              </div>
            ))
          ) : <p className="admin-muted">Geen recente errors gevonden</p>}
        </div>
      )}

      {activeSection === 'actions' && (
        <div className="admin-detail-section">
          <h4><Wrench size={14} /> Admin Acties</h4>
          <div className="admin-actions-grid">
            <div className="admin-action-card">
              <h5>2FA Resetten</h5>
              <p>Verwijdert 2FA code en markeert als geverifieerd</p>
              <button className="admin-btn-sm" onClick={() => onAction(u.id, 'reset-2fa')} data-testid="user-reset-2fa"><RefreshCw size={13} /> Reset 2FA</button>
            </div>

            <div className="admin-action-card">
              <h5>2FA Aan/Uit</h5>
              <p>Schakel two-factor authenticatie {u.two_factor_enabled ? 'uit' : 'in'} voor deze gebruiker</p>
              <button className={`admin-btn-sm ${u.two_factor_enabled ? '' : 'accent'}`} onClick={() => onAction(u.id, 'toggle-2fa', { enable: !u.two_factor_enabled })} data-testid="user-toggle-2fa">
                {u.two_factor_enabled ? <><ToggleRight size={13} /> 2FA Uitschakelen</> : <><ToggleLeft size={13} /> 2FA Inschakelen</>}
              </button>
            </div>

            {!u.is_verified && (
              <div className="admin-action-card">
                <h5>Handmatig Verifiëren</h5>
                <p>Markeer deze gebruiker als geverifieerd (skip email verificatie)</p>
                <button className="admin-btn-sm accent" onClick={() => onAction(u.id, 'verify')} data-testid="user-verify"><CheckCircle2 size={13} /> Verifiëren</button>
              </div>
            )}

            <div className="admin-action-card">
              <h5>Wachtwoord Resetten</h5>
              <input placeholder="Nieuw wachtwoord (standaard: Reset1234!)" value={newPw} onChange={e => setNewPw(e.target.value)} className="admin-action-input" />
              <button className="admin-btn-sm" onClick={handleResetPw} data-testid="user-reset-pw"><KeyRound size={13} /> Reset Wachtwoord</button>
              {pwResult && <p className="admin-action-result">{pwResult}</p>}
            </div>

            <div className="admin-action-card">
              <h5>Onboarding Voltooien</h5>
              <p>Markeer onboarding als voltooid</p>
              <button className="admin-btn-sm" onClick={() => onAction(u.id, 'complete-onboarding')} data-testid="user-complete-onboarding"><CheckCircle2 size={13} /> Voltooien</button>
            </div>

            <div className="admin-action-card">
              <h5>Impersonate</h5>
              <p>Opent een nieuw tabblad als deze gebruiker (voor support)</p>
              <button className="admin-btn-sm" onClick={handleImpersonate} data-testid="user-impersonate"><Eye size={13} /> Impersonate</button>
            </div>

            <div className="admin-action-card admin-action-danger">
              <h5>Account Verwijderen</h5>
              <p>Verwijdert het account en alle bijbehorende data permanent. Dit kan niet ongedaan worden gemaakt.</p>
              {!deleteConfirm ? (
                <button className="admin-btn-sm danger" onClick={() => setDeleteConfirm(true)} data-testid="user-delete-btn">
                  <Trash2 size={13} /> Verwijderen
                </button>
              ) : (
                <div className="admin-delete-confirm">
                  <p><strong>Weet je zeker dat je {u.full_name} ({u.email}) wilt verwijderen?</strong></p>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="admin-btn-sm danger" onClick={() => onDelete(u.id)} data-testid="user-delete-confirm">Ja, permanent verwijderen</button>
                    <button className="admin-btn-sm" onClick={() => setDeleteConfirm(false)}>Annuleren</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, num, label }) {
  return (
    <div className="admin-stat-card"><Icon size={16} /><span className="admin-stat-num">{num}</span><span className="admin-stat-label">{label}</span></div>
  );
}

function CreateDemoModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ full_name: '', email: '', company_name: '', password: 'Demo1234!' });
  const [result, setResult] = useState(null);
  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = await onCreate(form);
    if (data?.created) setResult(data);
  };
  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={e => e.stopPropagation()} data-testid="create-demo-modal">
        <h3><UserPlus size={18} /> Demo Gebruiker Aanmaken</h3>
        {result ? (
          <div className="admin-modal-result">
            <CheckCircle2 size={24} />
            <p>Email: <strong>{result.email}</strong></p>
            <p>Wachtwoord: <strong>{result.password}</strong></p>
            <button className="admin-btn" onClick={onClose}>Sluiten</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <input placeholder="Volledige naam" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} required />
            <input placeholder="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
            <input placeholder="Bedrijfsnaam" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} required />
            <input placeholder="Wachtwoord" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
            <div className="admin-modal-actions">
              <button type="button" className="admin-btn-secondary" onClick={onClose}>Annuleren</button>
              <button type="submit" className="admin-btn" data-testid="create-demo-submit">Aanmaken</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ANALYTICS PANEL
   ═══════════════════════════════════════════════════════════ */
function AnalyticsPanel({ headers }) {
  const [data, setData] = useState(null);
  useEffect(() => { fetch(`${API}/admin/analytics/overview`, { headers }).then(r => r.json()).then(setData).catch(() => {}); }, []);
  if (!data) return <Loading />;

  return (
    <div className="admin-analytics" data-testid="analytics-panel">
      <div className="admin-kpi-grid">
        <KPICard icon={Users} label="Totaal gebruikers" value={data.total_users} />
        <KPICard icon={Activity} label="Actief vandaag" value={data.active_today} color="green" />
        <KPICard icon={Activity} label="Actief (7d)" value={data.active_7d} />
        <KPICard icon={CheckCircle2} label="Geverifieerd" value={data.verified_users} />
        <KPICard icon={BarChart3} label="Onboarding rate" value={`${data.onboarding_rate}%`} color="blue" />
        <KPICard icon={MessageSquare} label="Chat sessies" value={data.total_sessions} />
      </div>

      <div className="admin-analytics-sections">
        <div className="admin-card">
          <h4><Zap size={15} /> Top Tools</h4>
          {(data.top_tools || []).length > 0 ? (
            <div className="admin-tool-bars">
              {(Array.isArray(data.top_tools) ? data.top_tools : []).map((t, i) => {
                const max = data.top_tools[0]?.count || 1;
                return (
                  <div key={i} className="admin-tool-bar-row">
                    <span className="admin-tool-name">{t.name}</span>
                    <div className="admin-tool-bar-track"><div className="admin-tool-bar-fill" style={{ width: `${(t.count / max) * 100}%` }} /></div>
                    <span className="admin-tool-count">{t.count}</span>
                  </div>
                );
              })}
            </div>
          ) : <p className="admin-muted">Nog geen tool gebruik</p>}
        </div>

        <div className="admin-card">
          <h4><BarChart3 size={15} /> Requests per dag (14d)</h4>
          {(data.ai_requests_daily || []).length > 0 ? (
            <div className="admin-reg-chart">
              {(Array.isArray(data.ai_requests_daily) ? data.ai_requests_daily : []).map((r, i) => {
                const max = Math.max(...(Array.isArray(data.ai_requests_daily) ? data.ai_requests_daily : []).map(d => d.count), 1);
                return (
                  <div key={i} className="admin-reg-bar" title={`${r.date}: ${r.count}`}>
                    <div className="admin-reg-bar-fill" style={{ height: `${Math.max(4, (r.count / max) * 60)}px` }} />
                    <span className="admin-reg-date">{r.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          ) : <p className="admin-muted">Geen data beschikbaar</p>}
        </div>
      </div>

      {(data.registrations_30d || []).length > 0 && (
        <div className="admin-card" style={{ marginTop: '1rem' }}>
          <h4><Users size={15} /> Registraties (30d)</h4>
          <div className="admin-reg-chart">
            {(Array.isArray(data.registrations_30d) ? data.registrations_30d : []).map((r, i) => (
              <div key={i} className="admin-reg-bar" title={`${r.date}: ${r.count}`}>
                <div className="admin-reg-bar-fill green" style={{ height: `${Math.max(4, r.count * 20)}px` }} />
                <span className="admin-reg-date">{r.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.recent_errors || []).length > 0 && (
        <div className="admin-card" style={{ marginTop: '1rem' }}>
          <h4><AlertTriangle size={15} /> Recente Platform Errors</h4>
          {(Array.isArray(data.recent_errors) ? data.recent_errors : []).map((e, i) => (
            <div key={i} className="admin-error-item">
              <span className="badge badge-inactive">{e.status_code || 'ERR'}</span>
              <span className="admin-error-path">{e.path || e.endpoint}</span>
              <span className="admin-notif-time">{e.timestamp ? new Date(e.timestamp).toLocaleString('nl-NL') : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KPICard({ icon: Icon, label, value, color }) {
  return (
    <div className={`admin-kpi-card ${color || ''}`}>
      <Icon size={18} /><span className="admin-kpi-value">{value}</span><span className="admin-kpi-label">{label}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   AI PANEL
   ═══════════════════════════════════════════════════════════ */
function AIPanel({ headers }) {
  const [config, setConfig] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [emmaProvider, setEmmaProvider] = useState('');
  const [emmaModel, setEmmaModel] = useState('');
  const [cloudProvider, setCloudProvider] = useState('');
  const [cloudModel, setCloudModel] = useState('');
  const [cloudKeys, setCloudKeys] = useState({ gemini_key: '', openai_key: '', anthropic_key: '' });
  const [ollamaUrl, setOllamaUrl] = useState('');
  const [ollamaModel, setOllamaModel] = useState('');
  const [saved, setSaved] = useState('');
  // Fallback
  const [fallbackProvider, setFallbackProvider] = useState('gemini');
  const [fallbackModel, setFallbackModel] = useState('gemini-2.0-flash');
  // Custom LLM
  const [customUrl, setCustomUrl] = useState('');
  const [customModel, setCustomModel] = useState('');
  const [customApiKey, setCustomApiKey] = useState('');
  const [customName, setCustomName] = useState('');

  const fetchConfig = () => {
    fetch(`${API}/admin/ai/config`, { headers }).then(r => r.json()).then(d => {
      setConfig(d);
      setEmmaProvider(d.emma?.provider || 'gemini');
      setEmmaModel(d.emma?.model || 'gemini-2.5-flash-lite');
      setCloudProvider(d.cloud?.provider || 'gemini');
      setCloudModel(d.cloud?.model || 'gemini-2.5-flash-lite');
      setOllamaUrl(d.ollama?.base_url || 'http://127.0.0.1:11434');
      setOllamaModel(d.ollama?.model || 'llama3.2');
    }).catch(() => {});
    // Also fetch cloud config for custom name + fallback
    fetch(`${API}/admin/ai/cloud`, { headers }).then(r => r.json()).then(d => {
      if (d.custom_name) setCustomName(d.custom_name);
      if (d.custom_url) setCustomUrl(d.custom_url);
      if (d.fallback_provider) setFallbackProvider(d.fallback_provider);
      if (d.fallback_model) setFallbackModel(d.fallback_model);
    }).catch(() => {});
  };

  useEffect(() => { fetchConfig(); }, []);

  const flash = (msg) => { setSaved(msg); setTimeout(() => setSaved(''), 3000); };

  const saveEmma = async () => {
    await fetch(`${API}/admin/ai/emma`, { method: 'PUT', headers, body: JSON.stringify({ provider: emmaProvider, model: emmaModel }) });
    flash('Emma config opgeslagen'); fetchConfig();
  };

  const saveCloud = async () => {
    const body = { provider: cloudProvider, model: cloudModel, ...cloudKeys, fallback_provider: fallbackProvider, fallback_model: fallbackModel };
    if (cloudProvider === 'custom') {
      body.custom_url = customUrl;
      body.custom_api_key = customApiKey;
      body.custom_name = customName;
    }
    await fetch(`${API}/admin/ai/cloud`, { method: 'PUT', headers, body: JSON.stringify(body) });
    setCloudKeys({ gemini_key: '', openai_key: '', anthropic_key: '' });
    flash('Cloud provider opgeslagen'); fetchConfig();
  };

  const saveOllama = async () => {
    await fetch(`${API}/admin/ai/ollama`, { method: 'PUT', headers, body: JSON.stringify({ base_url: ollamaUrl, model: ollamaModel }) });
    flash('Ollama config opgeslagen'); fetchConfig();
  };

  const testOllama = async () => {
    setTestLoading(true); setTestResult(null);
    try { const res = await fetch(`${API}/admin/ai/ollama/test`, { headers }); setTestResult(await res.json()); } catch (e) { setTestResult({ status: 'error', error: e.message }); }
    setTestLoading(false);
  };

  if (!config) return <Loading />;

  const providerModels = {
    gemini: ['gemini-3.1-pro', 'gemini-3-flash', 'gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.5-flash-lite', 'gemini-2.0-flash'],
    openai: ['gpt-5.4', 'o3', 'gpt-5.3-instant', 'o4-mini', 'gpt-4o', 'gpt-4o-mini'],
    anthropic: ['claude-opus-4.6', 'claude-sonnet-4.5', 'claude-haiku-4.6', 'claude-haiku-4.5'],
    ollama: config.ollama?.available_models?.length > 0
      ? config.ollama.available_models
      : ['llama3.2', 'llama3.1', 'mistral', 'phi3', 'gemma2', 'qwen2.5'],
  };

  const providerLabels = {
    gemini: 'Google Gemini',
    openai: 'OpenAI',
    anthropic: 'Anthropic Claude',
    ollama: 'Ollama (Lokaal)',
    custom: 'Custom LLM',
  };

  const keyStatus = (provider) => {
    if (provider === 'gemini') return config.cloud?.has_gemini_key;
    if (provider === 'openai') return config.cloud?.has_openai_key;
    if (provider === 'anthropic') return config.cloud?.has_anthropic_key;
    return false;
  };

  return (
    <div className="admin-ai" data-testid="ai-panel">
      {saved && <div className="admin-toast">{saved}</div>}

      {/* ── Cloud Provider (system-wide for heavy tools) ── */}
      <div className="admin-card admin-card-highlight">
        <h4><Shield size={16} /> Cloud Provider — Systeem-breed (zware tools)</h4>
        <p className="admin-card-desc">Deze provider wordt gebruikt door alle AI-tools wanneer gebruikers geen eigen API key hebben.</p>
        <div className="admin-emma-config">
          <div className="admin-detail-field">
            <label>Provider</label>
            <select className="admin-select" value={cloudProvider} onChange={e => { setCloudProvider(e.target.value); setCloudModel(providerModels[e.target.value]?.[0] || ''); }} data-testid="cloud-provider-select">
              <option value="gemini">Google Gemini</option>
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic Claude</option>
              <option value="custom">Custom LLM (OpenAI-compatible)</option>
            </select>
          </div>
          <div className="admin-detail-field">
            <label>Model</label>
            {cloudProvider === 'custom' ? (
              <input value={cloudModel} onChange={e => setCloudModel(e.target.value)} placeholder="Model naam (bijv. mistral-large)" className="admin-select" style={{ border: '1px solid rgba(255,255,255,0.15)' }} />
            ) : (
              <select className="admin-select" value={cloudModel} onChange={e => setCloudModel(e.target.value)} data-testid="cloud-model-select">
                {(providerModels[cloudProvider] || []).map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            )}
          </div>
          <button className="admin-btn" onClick={saveCloud} data-testid="save-cloud-config"><CheckCircle2 size={14} /> Opslaan</button>
        </div>

        {/* Custom LLM fields */}
        {cloudProvider === 'custom' && (
          <div className="admin-keys-section">
            <h5>Custom LLM Configuratie</h5>
            <div className="admin-keys-grid">
              <div className="admin-key-row">
                <span className="admin-key-label">Naam</span>
                <input type="text" placeholder="Bijv. Mistral, DeepSeek, Local LLM..." value={customName} onChange={e => setCustomName(e.target.value)} className="admin-key-input" data-testid="custom-llm-name" />
              </div>
              <div className="admin-key-row">
                <span className="admin-key-label">Base URL</span>
                <input type="text" placeholder="https://api.jouw-llm.nl/v1" value={customUrl} onChange={e => setCustomUrl(e.target.value)} className="admin-key-input" data-testid="custom-llm-url" />
              </div>
              <div className="admin-key-row">
                <span className="admin-key-label">API Key</span>
                <input type="password" placeholder="API key / secret..." value={customApiKey} onChange={e => setCustomApiKey(e.target.value)} className="admin-key-input" data-testid="custom-llm-key" />
              </div>
            </div>
          </div>
        )}

        <div className="admin-keys-section">
          <h5>API Keys</h5>
          <div className="admin-keys-grid">
            <div className="admin-key-row">
              <span className="admin-key-label">Gemini</span>
              <span className={`badge ${config.cloud?.has_gemini_key ? 'badge-active' : 'badge-inactive'}`}>
                {config.cloud?.has_gemini_key ? 'Geconfigureerd' : 'Ontbreekt'}
              </span>
              <input type="password" placeholder="Nieuwe key invoeren..." value={cloudKeys.gemini_key} onChange={e => setCloudKeys({...cloudKeys, gemini_key: e.target.value})} className="admin-key-input" />
            </div>
            <div className="admin-key-row">
              <span className="admin-key-label">OpenAI</span>
              <span className={`badge ${config.cloud?.has_openai_key ? 'badge-active' : 'badge-inactive'}`}>
                {config.cloud?.has_openai_key ? 'Geconfigureerd' : 'Ontbreekt'}
              </span>
              <input type="password" placeholder="Nieuwe key invoeren..." value={cloudKeys.openai_key} onChange={e => setCloudKeys({...cloudKeys, openai_key: e.target.value})} className="admin-key-input" />
            </div>
            <div className="admin-key-row">
              <span className="admin-key-label">Anthropic</span>
              <span className={`badge ${config.cloud?.has_anthropic_key ? 'badge-active' : 'badge-inactive'}`}>
                {config.cloud?.has_anthropic_key ? 'Geconfigureerd' : 'Ontbreekt'}
              </span>
              <input type="password" placeholder="Nieuwe key invoeren..." value={cloudKeys.anthropic_key} onChange={e => setCloudKeys({...cloudKeys, anthropic_key: e.target.value})} className="admin-key-input" />
            </div>
          </div>
        </div>

        {/* ── Fallback Provider ── */}
        <div className="admin-keys-section">
          <h5>Fallback Provider</h5>
          <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.5rem' }}>
            Wordt gebruikt als de primaire provider faalt of niet beschikbaar is.
          </p>
          <div className="admin-emma-config">
            <div className="admin-detail-field">
              <label>Provider</label>
              <select className="admin-select" value={fallbackProvider} onChange={e => { setFallbackProvider(e.target.value); setFallbackModel(providerModels[e.target.value]?.[0] || ''); }} data-testid="fallback-provider-select">
                <option value="gemini">Google Gemini</option>
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic Claude</option>
                <option value="ollama">Ollama (Lokaal)</option>
              </select>
            </div>
            <div className="admin-detail-field">
              <label>Model</label>
              {fallbackProvider === 'ollama' ? (
                <input value={fallbackModel} onChange={e => setFallbackModel(e.target.value)} placeholder="Model naam" className="admin-select" style={{ border: '1px solid rgba(255,255,255,0.15)' }} />
              ) : (
                <select className="admin-select" value={fallbackModel} onChange={e => setFallbackModel(e.target.value)} data-testid="fallback-model-select">
                  {(providerModels[fallbackProvider] || []).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              )}
            </div>
          </div>
        </div>

        <div className="admin-info-row" style={{ marginTop: '0.5rem' }}>
          <Cpu size={13} /> Actief: <strong>{config.cloud?.provider || 'gemini'}</strong> / <strong>{config.cloud?.model || 'gemini-2.5-flash-lite'}</strong>
          {!keyStatus(config.cloud?.provider) && <span className="badge badge-inactive">key ontbreekt</span>}
          <span style={{ margin: '0 6px', color: 'rgba(255,255,255,0.15)' }}>|</span>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Fallback: <strong>{fallbackProvider}</strong> / <strong>{fallbackModel}</strong></span>
        </div>
      </div>

      {/* ── Emma Avatar Provider ── */}
      <div className="admin-card">
        <h4><MessageSquare size={16} /> Emma Avatar — LLM Provider</h4>
        <p className="admin-card-desc">Aparte provider voor korte avatar-gesprekken. Kan lokaal via Ollama voor lage latency.</p>
        <div className="admin-emma-config">
          <div className="admin-detail-field">
            <label>Provider</label>
            <select className="admin-select" value={emmaProvider} onChange={e => { setEmmaProvider(e.target.value); setEmmaModel(providerModels[e.target.value]?.[0] || ''); }} data-testid="emma-provider-select">
              <option value="gemini">Gemini (Cloud)</option>
              <option value="openai">OpenAI (Cloud)</option>
              <option value="anthropic">Anthropic (Cloud)</option>
              <option value="ollama">Ollama (Lokaal)</option>
              <option value="custom">Custom LLM</option>
            </select>
          </div>
          <div className="admin-detail-field">
            <label>Model</label>
            {emmaProvider === 'ollama' || emmaProvider === 'custom' ? (
              <input value={emmaModel} onChange={e => setEmmaModel(e.target.value)} placeholder="Model naam" className="admin-select" style={{ border: '1px solid rgba(255,255,255,0.15)' }} />
            ) : (
              <select className="admin-select" value={emmaModel} onChange={e => setEmmaModel(e.target.value)} data-testid="emma-model-select">
                {(providerModels[emmaProvider] || []).map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            )}
          </div>
          <button className="admin-btn" onClick={saveEmma} data-testid="save-emma-config"><CheckCircle2 size={14} /> Opslaan</button>
        </div>
        <div className="admin-info-row" style={{ marginTop: '0.5rem' }}>
          <Cpu size={13} /> Actief: <strong>{config.emma?.provider}</strong> / <strong>{config.emma?.model}</strong>
          {config.emma?.provider === 'ollama' && <span className={`badge ${config.ollama?.status === 'online' ? 'badge-active' : 'badge-inactive'}`}>{config.ollama?.status}</span>}
        </div>
      </div>

      {/* ── Ollama Config ── */}
      <div className="admin-card">
        <h4><Cpu size={16} /> Ollama (Lokaal)</h4>
        <div className="admin-ollama-status">
          <span className={`badge ${config.ollama?.status === 'online' ? 'badge-active' : 'badge-inactive'}`}>
            {config.ollama?.status === 'online' ? 'Connected' : 'Offline'}
          </span>
          {config.ollama?.status === 'online' && config.ollama?.model_available && (
            <span style={{ fontSize: '0.75rem', color: '#10b981' }}>Model: {config.ollama.model}</span>
          )}
          {config.ollama?.status === 'online' && !config.ollama?.model_available && config.ollama?.model && (
            <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>Model "{config.ollama.model}" not found</span>
          )}
          {config.ollama?.available_models?.length > 0 && <span className="admin-muted" style={{ fontSize: '0.72rem' }}>{config.ollama.available_models.length} model(s)</span>}
        </div>
        <div className="admin-emma-config">
          <div className="admin-detail-field">
            <label>Base URL</label>
            <input value={ollamaUrl} onChange={e => setOllamaUrl(e.target.value)} className="admin-action-input" style={{ marginBottom: 0 }} />
          </div>
          <div className="admin-detail-field">
            <label>Standaard model</label>
            <input value={ollamaModel} onChange={e => setOllamaModel(e.target.value)} className="admin-action-input" style={{ marginBottom: 0 }} />
          </div>
          <button className="admin-btn-sm" onClick={saveOllama}><CheckCircle2 size={13} /> Opslaan</button>
          <button className="admin-btn-sm accent" onClick={testOllama} disabled={testLoading} data-testid="test-ollama">
            {testLoading ? <RefreshCw size={13} className="spin" /> : <><Zap size={13} /> Test</>}
          </button>
        </div>
        {config.ollama?.available_models?.length > 0 && (
          <div style={{ marginTop: '0.6rem' }}>
            <label style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>Beschikbare modellen</label>
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.3rem' }}>
              {(Array.isArray(config.ollama.available_models) ? config.ollama.available_models : []).map(m => <span key={m} className="badge badge-admin" style={{ cursor: 'pointer' }} onClick={() => setOllamaModel(m)}>{m}</span>)}
            </div>
          </div>
        )}
        {testResult && (
          <div className={`admin-test-result ${testResult.status}`} data-testid="ollama-test-result">
            {testResult.status === 'ok' ? (
              <><CheckCircle2 size={14} /> <strong>{testResult.model}</strong>: "{testResult.response}"</>
            ) : (
              <><AlertTriangle size={14} /> Error: {testResult.error}</>
            )}
          </div>
        )}
      </div>

      {/* ── TTS Voice Configuration ── */}
      <TTSPanel headers={headers} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   EMMA BRAIN PANEL — Memory Inspector & Management
   ═══════════════════════════════════════════════════════════ */
const LAYER_META = {
  semantic:   { label: 'Kennis',       icon: BookOpen,   color: '#10b981', desc: 'Stabiele feiten over het bedrijf' },
  episodic:   { label: 'Gebeurtenissen', icon: Clock,    color: '#6366f1', desc: 'Belangrijke interacties & events' },
  preference: { label: 'Voorkeuren',   icon: Star,       color: '#f59e0b', desc: 'Communicatie- & werkstijl' },
  reflective: { label: 'Geleerd',      icon: Lightbulb,  color: '#ec4899', desc: 'Patronen & verbeterpunten' },
};

const LEVEL_COLORS = ['#64748b', '#6366f1', '#10b981', '#f59e0b', '#ec4899'];
const LEVEL_BG     = ['#64748b22', '#6366f122', '#10b98122', '#f59e0b22', '#ec489922'];

function EmmaBrainPanel({ headers }) {
  const [stats, setStats] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [activeLayer, setActiveLayer] = useState(null);
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [maturity, setMaturity] = useState(null);
  const [goals, setGoals] = useState([]);
  const [pending, setPending] = useState([]);
  const [toolEff, setToolEff] = useState([]);
  const [section, setSection] = useState('overview');

  // Identity edit
  const [editName, setEditName] = useState('');
  const [editTone, setEditTone] = useState('');
  const [editMission, setEditMission] = useState('');
  const [editGoals, setEditGoals] = useState('');
  const [editTraits, setEditTraits] = useState('');

  // Add fact form
  const [newSubject, setNewSubject] = useState('');
  const [newContent, setNewContent] = useState('');

  // Goal form
  const [goalTitle, setGoalTitle] = useState('');
  const [goalDesc, setGoalDesc] = useState('');
  const [goalPriority, setGoalPriority] = useState('medium');
  const [goalDeadline, setGoalDeadline] = useState('');

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const fetchAll = async () => {
    try {
      const [statsR, matR, goalsR, pendR, toolR] = await Promise.all([
        fetch(`${API}/admin/emma/brain/stats`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/emma/maturity`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/emma/goals`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/emma/verification`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/emma/tools/effectiveness`, { headers }).then(r => r.json()),
      ]);
      setStats(statsR);
      setIdentity(statsR.identity);
      setEditName(statsR.identity?.emma_name || 'Emma');
      setEditTone(statsR.identity?.tone || '');
      setEditMission(statsR.identity?.mission || '');
      setEditGoals((statsR.identity?.active_goals || []).join('\n'));
      setEditTraits((statsR.identity?.behavioral_traits || []).join(', '));
      setMaturity(matR);
      setGoals(goalsR.goals || []);
      setPending(pendR.pending || []);
      setToolEff(toolR.tools || []);
    } catch {}
    setLoading(false);
  };

  const fetchLayer = async (layer) => {
    setActiveLayer(layer);
    setSection('memory');
    try {
      const r = await fetch(`${API}/admin/emma/brain/${layer}`, { headers });
      const d = await r.json();
      setMemories(d.memories || []);
    } catch { setMemories([]); }
  };

  const deleteMemory = async (layer, id) => {
    await fetch(`${API}/admin/emma/brain/${layer}/${id}`, { method: 'DELETE', headers });
    flash('Verwijderd');
    fetchLayer(layer);
    fetchAll();
  };

  const clearLayer = async (layer) => {
    if (!window.confirm(`Alle ${LAYER_META[layer]?.label} items verwijderen?`)) return;
    await fetch(`${API}/admin/emma/brain/${layer}`, { method: 'DELETE', headers });
    flash('Laag gewist');
    fetchLayer(layer);
    fetchAll();
  };

  const saveIdentity = async () => {
    await fetch(`${API}/admin/emma/identity`, {
      method: 'PUT', headers,
      body: JSON.stringify({
        emma_name: editName, tone: editTone, mission: editMission,
        active_goals: editGoals.split('\n').map(g => ((g) == null ? '' : String(g)).trim()).filter(Boolean),
        behavioral_traits: editTraits.split(',').map(t => ((t) == null ? '' : String(t)).trim()).filter(Boolean),
      }),
    });
    flash('Identiteit opgeslagen');
    fetchAll();
  };

  const addFact = async () => {
    if (!newSubject || !newContent) return;
    await fetch(`${API}/admin/emma/brain/semantic`, {
      method: 'POST', headers,
      body: JSON.stringify({ subject: newSubject, content: newContent, category: 'fact' }),
    });
    flash('Feit opgeslagen');
    setNewSubject(''); setNewContent('');
    fetchAll();
    if (activeLayer === 'semantic') fetchLayer('semantic');
  };

  const createGoal = async () => {
    if (!goalTitle) return;
    await fetch(`${API}/admin/emma/goals`, {
      method: 'POST', headers,
      body: JSON.stringify({ title: goalTitle, description: goalDesc, priority: goalPriority, deadline: goalDeadline }),
    });
    flash('Doel aangemaakt');
    setGoalTitle(''); setGoalDesc(''); setGoalDeadline('');
    fetchAll();
  };

  const updateGoalProgress = async (id, progress) => {
    await fetch(`${API}/admin/emma/goals/${id}`, {
      method: 'PUT', headers,
      body: JSON.stringify({ progress }),
    });
    fetchAll();
  };

  const completeGoal = async (id) => {
    await fetch(`${API}/admin/emma/goals/${id}`, {
      method: 'PUT', headers,
      body: JSON.stringify({ status: 'completed', progress: 100 }),
    });
    flash('Doel voltooid!');
    fetchAll();
  };

  const deleteGoal = async (id) => {
    await fetch(`${API}/admin/emma/goals/${id}`, { method: 'DELETE', headers });
    flash('Doel verwijderd');
    fetchAll();
  };

  const verifyItem = async (id, approved) => {
    await fetch(`${API}/admin/emma/verification/${id}`, {
      method: 'PUT', headers,
      body: JSON.stringify({ approved }),
    });
    flash(approved ? 'Goedgekeurd als feit' : 'Afgewezen en verwijderd');
    fetchAll();
  };

  const runConsolidation = async () => {
    flash('Consolidatie bezig...');
    await fetch(`${API}/admin/emma/brain/consolidate`, { method: 'POST', headers });
    flash('Consolidatie voltooid');
    fetchAll();
  };

  useEffect(() => { fetchAll(); }, []);

  if (loading) return <div className="admin-loading">Laden...</div>;

  const lv = maturity?.level || 1;
  const policy = stats?.memory_policy || {};

  const SECTIONS = [
    { id: 'overview', label: 'Overzicht', icon: Brain },
    { id: 'goals', label: 'Doelen', icon: Target },
    { id: 'memory', label: 'Geheugen', icon: Database },
    { id: 'verify', label: `Verificatie ${pending.length ? `(${pending.length})` : ''}`, icon: Shield },
    { id: 'tools', label: 'Tool Effectiviteit', icon: Wrench },
    { id: 'identity', label: 'Identiteit', icon: Sparkles },
  ];

  return (
    <div data-testid="emma-brain-panel">
      {msg && <div className="admin-toast">{msg}</div>}

      {/* ── Section Tabs ── */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {(Array.isArray(SECTIONS) ? SECTIONS : []).map(s => (
          <button key={s.id} data-testid={`emma-section-${s.id}`}
            onClick={() => setSection(s.id)}
            style={{
              padding: '0.4rem 0.75rem', borderRadius: '6px', border: 'none', cursor: 'pointer',
              fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.3rem',
              background: section === s.id ? 'var(--accent)' : 'var(--card-bg)',
              color: section === s.id ? '#fff' : 'var(--text-secondary)',
              fontWeight: section === s.id ? 600 : 400,
            }}
          >
            <s.icon size={13} /> {s.label}
          </button>
        ))}
      </div>

      {/* ═══ OVERVIEW ═══ */}
      {section === 'overview' && (<>
        {/* Maturity Level */}
        <div className="admin-card" data-testid="emma-maturity">
          <h4><Brain size={16} /> Emma Maturity Level</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', margin: '1rem 0' }}>
            <div style={{
              width: 80, height: 80, borderRadius: '50%', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              background: LEVEL_BG[lv - 1], border: `3px solid ${LEVEL_COLORS[lv - 1]}`,
            }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: LEVEL_COLORS[lv - 1], lineHeight: 1 }}>{lv}</div>
              <div style={{ fontSize: '0.55rem', color: LEVEL_COLORS[lv - 1], fontWeight: 600 }}>{maturity?.level_name}</div>
            </div>
            <div style={{ flex: 1 }}>
              {maturity?.criteria && Object.entries(maturity.criteria).map(([key, c]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.2rem 0', fontSize: '0.78rem' }}>
                  {c.met
                    ? <CheckCircle2 size={14} style={{ color: '#10b981' }} />
                    : <XCircle size={14} style={{ color: '#64748b44' }} />}
                  <span style={{ fontWeight: c.met ? 600 : 400, opacity: c.met ? 1 : 0.5 }}>{c.label}</span>
                  <span style={{ opacity: 0.4, fontSize: '0.7rem', marginLeft: 'auto' }}>{c.detail}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Memory Budget */}
        <div className="admin-card" data-testid="emma-budget">
          <h4><Database size={16} /> Geheugen Budget</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', margin: '0.75rem 0' }}>
            {[
              { key: 'semantic_count', max: policy.max_semantic || 100, label: 'Kennis', color: '#10b981' },
              { key: 'episodic_count', max: policy.max_episodic || 200, label: 'Events', color: '#6366f1' },
              { key: 'preference_count', max: policy.max_preferences || 50, label: 'Voorkeuren', color: '#f59e0b' },
              { key: 'reflective_count', max: policy.max_reflections || 50, label: 'Geleerd', color: '#ec4899' },
            ].map(b => {
              const count = stats?.[b.key] || 0;
              const pct = Math.min(100, (count / b.max) * 100);
              const warn = pct > 80;
              return (
                <div key={b.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: 600 }}>{b.label}</span>
                    <span style={{ color: warn ? '#ef4444' : 'inherit' }}>{count} / {b.max}{warn ? ' !' : ''}</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--border)', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: warn ? '#ef4444' : b.color, borderRadius: 3, transition: 'width 0.3s' }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="admin-btn-sm accent" onClick={runConsolidation} data-testid="emma-consolidate">
              <RefreshCw size={13} /> Consolidatie
            </button>
          </div>
        </div>

        {/* Quick Memory Layer Cards */}
        <div className="admin-card" data-testid="emma-brain-stats">
          <h4>Geheugenlagen</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.5rem', margin: '0.5rem 0' }}>
            {Object.entries(LAYER_META).map(([key, meta]) => {
              const count = stats?.[`${key}_count`] || 0;
              return (
                <button key={key} data-testid={`emma-layer-${key}`} onClick={() => fetchLayer(key)}
                  className="admin-card" style={{ cursor: 'pointer', textAlign: 'left', padding: '0.75rem', border: `1px solid var(--border)` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <meta.icon size={14} style={{ color: meta.color }} />
                    <strong style={{ fontSize: '0.8rem' }}>{meta.label}</strong>
                  </div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: meta.color }}>{count}</div>
                </button>
              );
            })}
          </div>
        </div>
      </>)}

      {/* ═══ GOALS ═══ */}
      {section === 'goals' && (<>
        <div className="admin-card" data-testid="emma-goals-panel">
          <h4><Target size={16} /> Actieve Doelen</h4>
          <p className="admin-card-desc">Emma koppelt interacties aan doelen en volgt voortgang automatisch.</p>

          {goals.length === 0 && <p style={{ opacity: 0.5, padding: '0.5rem 0' }}>Nog geen doelen ingesteld.</p>}

          {(Array.isArray(goals) ? goals : []).map(g => (
            <div key={g.id} className="admin-card" style={{ padding: '0.75rem', marginBottom: '0.5rem' }} data-testid={`goal-${g.id}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    <span style={{
                      display: 'inline-block', padding: '0.1rem 0.4rem', borderRadius: 4, fontSize: '0.65rem', marginRight: '0.4rem',
                      background: g.priority === 'high' ? '#ef444422' : g.priority === 'low' ? '#64748b22' : '#f59e0b22',
                      color: g.priority === 'high' ? '#ef4444' : g.priority === 'low' ? '#64748b' : '#f59e0b',
                    }}>
                      {g.priority === 'high' ? 'HOOG' : g.priority === 'low' ? 'LAAG' : 'MIDDEN'}
                    </span>
                    {g.title}
                    {g.status === 'completed' && <CheckCircle2 size={14} style={{ color: '#10b981', marginLeft: '0.3rem' }} />}
                  </div>
                  {g.description && <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.2rem' }}>{g.description}</div>}
                  {g.deadline && <div style={{ fontSize: '0.7rem', opacity: 0.5 }}>Deadline: {g.deadline}</div>}
                </div>
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  {g.status !== 'completed' && (
                    <button className="admin-btn-sm accent" onClick={() => completeGoal(g.id)} data-testid={`goal-complete-${g.id}`}>
                      <CheckCircle2 size={12} />
                    </button>
                  )}
                  <button className="admin-btn-sm" style={{ color: '#ef4444' }} onClick={() => deleteGoal(g.id)}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              {/* Progress bar */}
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', marginBottom: '0.2rem' }}>
                  <span>Voortgang</span><span>{g.progress}%</span>
                </div>
                <div style={{ height: 6, background: 'var(--border)', borderRadius: 3 }}>
                  <div style={{ height: '100%', width: `${g.progress}%`, background: g.progress >= 100 ? '#081D14' : '#6366f1', borderRadius: 3 }} />
                </div>
                {g.status !== 'completed' && (
                  <input type="range" min={0} max={100} value={g.progress}
                    onChange={e => updateGoalProgress(g.id, parseInt(e.target.value))}
                    style={{ width: '100%', marginTop: '0.3rem' }} data-testid={`goal-slider-${g.id}`} />
                )}
              </div>
              {/* Milestones */}
              {g.milestones?.length > 0 && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.7rem', opacity: 0.5 }}>
                  {g.milestones.slice(-3).map((m, i) => (
                    <div key={i}>- {m.text} ({m.date?.slice(0, 10)})</div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* New Goal Form */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem', marginTop: '0.75rem' }}>
            <h5 style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}><Plus size={14} /> Nieuw doel</h5>
            <div className="admin-emma-config" style={{ flexWrap: 'wrap' }}>
              <div className="admin-detail-field" style={{ flex: 2, minWidth: 200 }}>
                <label>Titel</label>
                <input className="admin-key-input" value={goalTitle} onChange={e => setGoalTitle(e.target.value)}
                  placeholder="bijv. Omzet verhogen naar 3.5M" data-testid="emma-goal-title" />
              </div>
              <div className="admin-detail-field">
                <label>Prioriteit</label>
                <select className="admin-select" value={goalPriority} onChange={e => setGoalPriority(e.target.value)} data-testid="emma-goal-priority">
                  <option value="high">Hoog</option>
                  <option value="medium">Midden</option>
                  <option value="low">Laag</option>
                </select>
              </div>
              <div className="admin-detail-field">
                <label>Deadline</label>
                <input className="admin-key-input" type="date" value={goalDeadline} onChange={e => setGoalDeadline(e.target.value)} data-testid="emma-goal-deadline" />
              </div>
            </div>
            <div className="admin-detail-field" style={{ marginTop: '0.4rem' }}>
              <label>Beschrijving</label>
              <input className="admin-key-input" value={goalDesc} onChange={e => setGoalDesc(e.target.value)}
                placeholder="bijv. Klantbasis uitbreiden in financiele sector" data-testid="emma-goal-desc" />
            </div>
            <button className="admin-btn" onClick={createGoal} style={{ marginTop: '0.5rem' }} data-testid="emma-goal-save">
              <Save size={14} /> Doel aanmaken
            </button>
          </div>
        </div>
      </>)}

      {/* ═══ MEMORY DETAIL ═══ */}
      {section === 'memory' && (<>
        {/* Layer cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {Object.entries(LAYER_META).map(([key, meta]) => {
            const count = stats?.[`${key}_count`] || 0;
            const isActive = activeLayer === key;
            return (
              <button key={key} data-testid={`emma-layer-${key}`} onClick={() => fetchLayer(key)}
                className="admin-card" style={{
                  cursor: 'pointer', textAlign: 'left', padding: '0.75rem',
                  border: isActive ? `2px solid ${meta.color}` : '1px solid var(--border)',
                  background: isActive ? `${meta.color}11` : 'transparent',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <meta.icon size={14} style={{ color: meta.color }} />
                  <strong style={{ fontSize: '0.8rem' }}>{meta.label}</strong>
                </div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: meta.color }}>{count}</div>
              </button>
            );
          })}
        </div>

        {activeLayer && (
          <div className="admin-card" data-testid="emma-layer-detail">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ color: LAYER_META[activeLayer]?.color }}>
                {React.createElement(LAYER_META[activeLayer]?.icon, { size: 16 })} {LAYER_META[activeLayer]?.label} ({memories.length})
              </h4>
              <button className="admin-btn-sm" style={{ color: '#ef4444' }} onClick={() => clearLayer(activeLayer)}>
                <Trash2 size={13} /> Alles wissen
              </button>
            </div>
            {memories.length === 0 && <p style={{ opacity: 0.5, padding: '0.5rem 0' }}>Geen items.</p>}
            <div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '0.5rem' }}>
              {(Array.isArray(memories) ? memories : []).map((m, i) => (
                <div key={m.id || i} className="admin-card" style={{ padding: '0.6rem', marginBottom: '0.4rem', fontSize: '0.78rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      {m.subject && <div style={{ fontWeight: 600 }}>{m.subject}</div>}
                      {m.summary && <div style={{ fontWeight: 600 }}>{m.summary}</div>}
                      {m.key && <div style={{ fontWeight: 600 }}>{m.key}: {m.value}</div>}
                      {m.content && <div style={{ opacity: 0.8 }}>{m.content?.substring(0, 200)}</div>}
                      <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.3rem', opacity: 0.5, fontSize: '0.68rem', flexWrap: 'wrap' }}>
                        {m.category && <span>{m.category === 'fact' ? 'FEIT' : m.category === 'inference' ? 'GEVOLGTREKKING' : 'HYPOTHESE'}</span>}
                        {m.confidence_score != null && <span>Zekerheid: {(m.confidence_score * 100).toFixed(0)}%</span>}
                        {m.importance_score != null && <span>Belang: {(m.importance_score * 100).toFixed(0)}%</span>}
                        {m.human_verified && <span style={{ color: '#10b981' }}>Geverifieerd</span>}
                        {m.outcome && <span>Uitkomst: {m.outcome}</span>}
                        {m.observation_count && <span>Gezien: {m.observation_count}x</span>}
                      </div>
                    </div>
                    {m.id && (
                      <button className="admin-btn-sm" style={{ color: '#ef4444', flexShrink: 0 }}
                        onClick={() => deleteMemory(activeLayer, m.id)} data-testid={`emma-delete-${m.id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Fact */}
        <div className="admin-card" style={{ marginTop: '0.75rem' }} data-testid="emma-add-fact">
          <h4><Plus size={16} /> Feit toevoegen</h4>
          <div className="admin-emma-config">
            <div className="admin-detail-field">
              <label>Onderwerp</label>
              <input className="admin-key-input" value={newSubject} onChange={e => setNewSubject(e.target.value)}
                placeholder="bijv. Hoofdkantoor" data-testid="emma-fact-subject" />
            </div>
            <div className="admin-detail-field">
              <label>Inhoud</label>
              <input className="admin-key-input" value={newContent} onChange={e => setNewContent(e.target.value)}
                placeholder="bijv. Rotterdam, Nederland" data-testid="emma-fact-content" />
            </div>
          </div>
          <button className="admin-btn" onClick={addFact} style={{ marginTop: '0.5rem' }} data-testid="emma-fact-save">
            <Save size={14} /> Opslaan
          </button>
        </div>
      </>)}

      {/* ═══ VERIFICATION QUEUE ═══ */}
      {section === 'verify' && (
        <div className="admin-card" data-testid="emma-verification">
          <h4><Shield size={16} /> Verificatie Wachtrij</h4>
          <p className="admin-card-desc">Emma's onzekere gevolgtrekkingen die menselijke bevestiging nodig hebben. Goedgekeurde items worden feiten met hoge zekerheid.</p>

          {pending.length === 0 && <p style={{ opacity: 0.5, padding: '1rem 0' }}>Geen items wachten op verificatie.</p>}

          {(Array.isArray(pending) ? pending : []).map(m => (
            <div key={m.id} className="admin-card" style={{ padding: '0.75rem', marginBottom: '0.5rem' }} data-testid={`verify-${m.id}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{m.subject}</div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: '0.2rem' }}>{m.content}</div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.4, marginTop: '0.3rem' }}>
                    <span style={{ marginRight: '0.6rem' }}>{m.category === 'inference' ? 'GEVOLGTREKKING' : 'HYPOTHESE'}</span>
                    <span style={{ marginRight: '0.6rem' }}>Zekerheid: {(m.confidence_score * 100).toFixed(0)}%</span>
                    <span>Bron: {m.source}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                  <button className="admin-btn-sm accent" onClick={() => verifyItem(m.id, true)} data-testid={`approve-${m.id}`}>
                    <CheckCircle2 size={14} /> Goedkeuren
                  </button>
                  <button className="admin-btn-sm" style={{ color: '#ef4444' }} onClick={() => verifyItem(m.id, false)} data-testid={`reject-${m.id}`}>
                    <XCircle size={14} /> Afwijzen
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ TOOL EFFECTIVENESS ═══ */}
      {section === 'tools' && (
        <div className="admin-card" data-testid="emma-tool-eff">
          <h4><Wrench size={16} /> Tool Effectiviteit</h4>
          <p className="admin-card-desc">Emma leert welke tools het beste werken in welke context op basis van resultaten.</p>

          {toolEff.length === 0 && <p style={{ opacity: 0.5, padding: '1rem 0' }}>Nog geen tool-leerdata. Gebruik tools via Emma om data op te bouwen.</p>}

          {(Array.isArray(toolEff) ? toolEff : []).map((t, i) => {
            const eff = (t.effectiveness_score * 100).toFixed(0);
            const effColor = t.effectiveness_score >= 0.7 ? '#10b981' : t.effectiveness_score >= 0.4 ? '#f59e0b' : '#ef4444';
            return (
              <div key={t.id || i} className="admin-card" style={{ padding: '0.6rem', marginBottom: '0.4rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{t.tool_name}</div>
                    <div style={{ fontSize: '0.72rem', opacity: 0.5 }}>Context: {t.context} | {t.total_uses}x gebruikt | {t.success_count} succes / {t.failure_count} fout</div>
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: effColor }}>{eff}%</div>
                </div>
                <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, marginTop: '0.4rem' }}>
                  <div style={{ height: '100%', width: `${eff}%`, background: effColor, borderRadius: 2 }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ IDENTITY ═══ */}
      {section === 'identity' && (
        <div className="admin-card" data-testid="emma-identity-panel">
          <h4><Sparkles size={16} /> Emma Identiteit</h4>
          <p className="admin-card-desc">Configureer Emma's persoonlijkheid en gedragsregels.</p>
          <div className="admin-emma-config" style={{ flexWrap: 'wrap' }}>
            <div className="admin-detail-field">
              <label>Naam</label>
              <input className="admin-key-input" value={editName} onChange={e => setEditName(e.target.value)} data-testid="emma-name" />
            </div>
            <div className="admin-detail-field">
              <label>Toon</label>
              <select className="admin-select" value={editTone} onChange={e => setEditTone(e.target.value)} data-testid="emma-tone">
                {['friendly_professional', 'formal', 'casual', 'coaching', 'analytical'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="admin-detail-field" style={{ marginTop: '0.5rem' }}>
            <label>Missie</label>
            <input className="admin-key-input" value={editMission} onChange={e => setEditMission(e.target.value)} data-testid="emma-mission" />
          </div>
          <div className="admin-detail-field" style={{ marginTop: '0.5rem' }}>
            <label>Actieve doelen (1 per regel)</label>
            <textarea className="admin-key-input" rows={3} value={editGoals} onChange={e => setEditGoals(e.target.value)}
              placeholder="bijv. Omzet verhogen Q2&#10;Kosten reduceren" data-testid="emma-goals" />
          </div>
          <div className="admin-detail-field" style={{ marginTop: '0.5rem' }}>
            <label>Gedragskenmerken (komma-gescheiden)</label>
            <input className="admin-key-input" value={editTraits} onChange={e => setEditTraits(e.target.value)}
              placeholder="bijv. beknopt, proactief, analytisch" data-testid="emma-traits" />
          </div>
          <button className="admin-btn" onClick={saveIdentity} style={{ marginTop: '0.75rem' }} data-testid="emma-identity-save">
            <Save size={14} /> Identiteit opslaan
          </button>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   TTS VOICE PANEL
   ═══════════════════════════════════════════════════════════ */
function TTSPanel({ headers }) {
  const [cfg, setCfg] = useState(null);
  const [voiceNl, setVoiceNl] = useState('');
  const [voiceEn, setVoiceEn] = useState('');
  const [rate, setRate] = useState('');
  const [pitch, setPitch] = useState('');
  const [saved, setSaved] = useState('');
  const [testing, setTesting] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  const fetchCfg = () => {
    fetch(`${API}/admin/tts/config`, { headers }).then(r => r.json()).then(d => {
      setCfg(d);
      setVoiceNl(d.voice_nl);
      setVoiceEn(d.voice_en);
      setRate(d.rate);
      setPitch(d.pitch);
    }).catch(() => {});
  };
  useEffect(() => { fetchCfg(); }, []);

  const flash = (msg) => { setSaved(msg); setTimeout(() => setSaved(''), 3000); };

  const saveTts = async () => {
    await fetch(`${API}/admin/tts/config`, {
      method: 'PUT', headers,
      body: JSON.stringify({ voice_nl: voiceNl, voice_en: voiceEn, rate, pitch }),
    });
    flash('TTS configuratie opgeslagen + cache geleegd');
    fetchCfg();
  };

  const testVoice = async (lang) => {
    setTesting(true);
    if (audioUrl) { URL.revokeObjectURL(audioUrl); setAudioUrl(null); }
    try {
      const text = lang === 'nl'
        ? 'Hallo, ik ben Emma. Hoe kan ik je vandaag helpen?'
        : 'Hello, I am Emma. How can I help you today?';
      const res = await fetch(`${API}/tts/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, lang, voice: lang === 'nl' ? voiceNl : voiceEn }),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        const audio = new Audio(url);
        audio.play();
      }
    } catch {}
    setTesting(false);
  };

  if (!cfg) return null;

  return (
    <div className="admin-card" data-testid="tts-panel">
      <h4><MessageSquare size={16} /> TTS Stem Configuratie</h4>
      <p className="admin-card-desc">Kies de stem, snelheid en toonhoogte voor Emma's spraak. Wijzigingen worden direct toegepast (geen herstart nodig).</p>
      {saved && <div className="admin-toast">{saved}</div>}

      <div className="admin-emma-config" style={{ flexWrap: 'wrap' }}>
        <div className="admin-detail-field">
          <label>Nederlands stem</label>
          <select className="admin-select" value={voiceNl} onChange={e => setVoiceNl(e.target.value)} data-testid="tts-voice-nl">
            {(cfg.available_nl || []).map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="admin-detail-field">
          <label>Engels stem</label>
          <select className="admin-select" value={voiceEn} onChange={e => setVoiceEn(e.target.value)} data-testid="tts-voice-en">
            {(cfg.available_en || []).map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="admin-detail-field">
          <label>Snelheid</label>
          <select className="admin-select" value={rate} onChange={e => setRate(e.target.value)} data-testid="tts-rate">
            {['-20%', '-15%', '-10%', '-5%', '+0%', '+5%', '+10%', '+15%', '+18%', '+20%', '+25%'].map(v => (
              <option key={v} value={v}>{v}{v === '+10%' ? ' (aanbevolen)' : ''}</option>
            ))}
          </select>
        </div>
        <div className="admin-detail-field">
          <label>Toonhoogte</label>
          <select className="admin-select" value={pitch} onChange={e => setPitch(e.target.value)} data-testid="tts-pitch">
            {['-10Hz', '-8Hz', '-5Hz', '-4Hz', '-2Hz', '+0Hz', '+2Hz', '+4Hz', '+5Hz', '+8Hz', '+10Hz'].map(v => (
              <option key={v} value={v}>{v}{v === '-2Hz' ? ' (aanbevolen)' : ''}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
        <button className="admin-btn" onClick={saveTts} data-testid="save-tts-config"><Save size={14} /> Opslaan</button>
        <button className="admin-btn-sm accent" onClick={() => testVoice('nl')} disabled={testing} data-testid="test-tts-nl">
          {testing ? <RefreshCw size={13} className="spin" /> : <><Zap size={13} /> Test NL</>}
        </button>
        <button className="admin-btn-sm" onClick={() => testVoice('en')} disabled={testing} data-testid="test-tts-en">
          {testing ? <RefreshCw size={13} className="spin" /> : <><Zap size={13} /> Test EN</>}
        </button>
      </div>

      <div className="admin-info-row" style={{ marginTop: '0.5rem' }}>
        <Cpu size={13} /> Actief: <strong>{cfg.voice_nl}</strong> / <strong>{cfg.voice_en}</strong> — snelheid: <strong>{cfg.rate}</strong>, toon: <strong>{cfg.pitch}</strong>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   EMAIL / CHAT PANEL
   ═══════════════════════════════════════════════════════════ */
function EmailPanel({ headers }) {
  const [stats, setStats] = useState(null);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [setupMsg, setSetupMsg] = useState('');
  const [flashMsg, setFlashMsg] = useState('');

  // SendPulse form
  const [spApiId, setSpApiId] = useState('');
  const [spApiSecret, setSpApiSecret] = useState('');
  const [spSenderEmail, setSpSenderEmail] = useState('');
  const [spSenderName, setSpSenderName] = useState('');

  // Telegram form
  const [tgBotToken, setTgBotToken] = useState('');
  const [tgChatId, setTgChatId] = useState('');
  const [tgWebhookUrl, setTgWebhookUrl] = useState('');

  const flash = (msg) => { setFlashMsg(msg); setTimeout(() => setFlashMsg(''), 3000); };

  const fetchStats = () => {
    fetch(`${API}/admin/email/stats`, { headers }).then(r => r.json()).then(d => {
      setStats(d);
      // Pre-fill forms
      setSpApiId(d.sendpulse?.api_id || '');
      setSpSenderEmail(d.sendpulse?.sender_email || '');
      setSpSenderName(d.sendpulse?.sender_name || '');
      setTgChatId(d.telegram?.chat_id || '');
      setTgWebhookUrl(d.telegram?.webhook_url || '');
    }).catch(() => {});
    fetch(`${API}/admin/telegram/webhook-status`, { headers }).then(r => r.json()).then(setWebhookStatus).catch(() => {});
  };
  useEffect(() => { fetchStats(); }, []);

  const saveEmail = async () => {
    const body = { sendpulse_api_id: spApiId, sender_email: spSenderEmail, sender_name: spSenderName };
    if (spApiSecret) body.sendpulse_api_secret = spApiSecret;
    await fetch(`${API}/admin/email/config`, { method: 'PUT', headers, body: JSON.stringify(body) });
    flash('SendPulse configuratie opgeslagen');
    fetchStats();
  };

  const saveTelegram = async () => {
    const body = { chat_id: tgChatId, webhook_url: tgWebhookUrl };
    if (tgBotToken) body.bot_token = tgBotToken;
    await fetch(`${API}/admin/telegram/config`, { method: 'PUT', headers, body: JSON.stringify(body) });
    flash('Telegram configuratie opgeslagen');
    fetchStats();
  };

  const setupWebhook = async () => {
    setSetupMsg('');
    try {
      const res = await fetch(`${API}/admin/telegram/setup-webhook`, { method: 'POST', headers });
      const data = await res.json();
      if (res.ok) { setSetupMsg(`Webhook ingesteld: ${data.webhook_url}`); fetchStats(); }
      else setSetupMsg(`Fout: ${data.detail}`);
    } catch (e) { setSetupMsg(`Fout: ${e.message}`); }
  };

  if (!stats) return <Loading />;

  return (
    <div className="admin-system" data-testid="email-panel">
      <div className="admin-system-header">
        <h4><Mail size={16} /> Email & Chat Configuratie</h4>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {flashMsg && <span style={{ color: '#10b981', fontSize: '0.8rem', fontWeight: 500 }}>{flashMsg}</span>}
          <button className="admin-btn-sm" onClick={fetchStats} data-testid="refresh-email"><RefreshCw size={14} /> Vernieuwen</button>
        </div>
      </div>

      <div className="admin-system-grid">
        {/* SendPulse Configuration */}
        <div className="admin-card">
          <h4><Mail size={16} /> SendPulse Email</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span className={`admin-status-dot ${stats.sendpulse.configured ? 'ok' : 'error'}`} />
            <span style={{ fontSize: '0.8rem', color: stats.sendpulse.configured ? '#10b981' : '#ef4444' }}>
              {stats.sendpulse.configured ? 'Geconfigureerd' : 'Niet geconfigureerd'}
            </span>
          </div>
          <div className="admin-keys-grid">
            <div className="admin-key-row">
              <span className="admin-key-label">API ID</span>
              <input type="text" value={spApiId} onChange={e => setSpApiId(e.target.value)} className="admin-key-input" data-testid="sp-api-id" placeholder="SendPulse Client ID" />
            </div>
            <div className="admin-key-row">
              <span className="admin-key-label">API Secret</span>
              <input type="password" value={spApiSecret} onChange={e => setSpApiSecret(e.target.value)} className="admin-key-input" data-testid="sp-api-secret" placeholder={stats.sendpulse.has_secret ? '(ingesteld — laat leeg om te behouden)' : 'SendPulse Client Secret'} />
            </div>
            <div className="admin-key-row">
              <span className="admin-key-label">Afzender Email</span>
              <input type="email" value={spSenderEmail} onChange={e => setSpSenderEmail(e.target.value)} className="admin-key-input" data-testid="sp-sender-email" placeholder="noreply@krypton9.com" />
            </div>
            <div className="admin-key-row">
              <span className="admin-key-label">Afzender Naam</span>
              <input type="text" value={spSenderName} onChange={e => setSpSenderName(e.target.value)} className="admin-key-input" data-testid="sp-sender-name" placeholder="Krypton9" />
            </div>
          </div>
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
            <button className="admin-btn-sm accent" onClick={saveEmail} data-testid="save-email-config"><Save size={13} /> Opslaan</button>
          </div>
          {stats.sendpulse.stats && !stats.sendpulse.stats.error && (
            <div className="admin-config-grid" style={{ marginTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '0.75rem' }}>
              <ConfigRow label="Totaal verzonden" value={stats.sendpulse.stats.total || '0'} />
              <ConfigRow label="Afgeleverd" value={stats.sendpulse.stats.delivered || '—'} />
              <ConfigRow label="Bounced" value={stats.sendpulse.stats.bounced || '0'} />
            </div>
          )}
          {stats.sendpulse.stats?.error && <ConfigRow label="API Error" value={stats.sendpulse.stats.error} status="error" />}
        </div>

        {/* Telegram Configuration */}
        <div className="admin-card">
          <h4><MessageSquare size={16} /> Telegram Bot</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span className={`admin-status-dot ${stats.telegram.configured ? 'ok' : 'error'}`} />
            <span style={{ fontSize: '0.8rem', color: stats.telegram.configured ? '#10b981' : '#ef4444' }}>
              {stats.telegram.configured ? 'Geconfigureerd' : 'Niet geconfigureerd'}
            </span>
          </div>
          <div className="admin-keys-grid">
            <div className="admin-key-row">
              <span className="admin-key-label">Bot Token</span>
              <input type="password" value={tgBotToken} onChange={e => setTgBotToken(e.target.value)} className="admin-key-input" data-testid="tg-bot-token" placeholder={stats.telegram.has_token ? `${stats.telegram.token_preview} (laat leeg om te behouden)` : 'Telegram Bot Token'} />
            </div>
            <div className="admin-key-row">
              <span className="admin-key-label">Chat ID</span>
              <input type="text" value={tgChatId} onChange={e => setTgChatId(e.target.value)} className="admin-key-input" data-testid="tg-chat-id" placeholder="Bijv. 8625553090" />
            </div>
            <div className="admin-key-row">
              <span className="admin-key-label">Webhook URL</span>
              <input type="text" value={tgWebhookUrl} onChange={e => setTgWebhookUrl(e.target.value)} className="admin-key-input" data-testid="tg-webhook-url" placeholder="https://krypton9.com/api/homepage/telegram-webhook" />
            </div>
          </div>
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="admin-btn-sm accent" onClick={saveTelegram} data-testid="save-telegram-config"><Save size={13} /> Opslaan</button>
            {stats.telegram.configured && (
              <button className="admin-btn-sm" onClick={setupWebhook} data-testid="setup-telegram-webhook">
                <ExternalLink size={14} /> Webhook Activeren
              </button>
            )}
          </div>
          {setupMsg && <p style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: setupMsg.startsWith('Fout') ? '#ef4444' : '#10b981' }}>{setupMsg}</p>}
          {webhookStatus && (
            <div className="admin-config-grid" style={{ marginTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '0.75rem' }}>
              <ConfigRow label="Actieve Webhook" value={webhookStatus.webhook_url || 'Niet ingesteld'} status={webhookStatus.webhook_url ? 'ok' : 'warning'} />
              <ConfigRow label="Wachtend" value={`${webhookStatus.pending_updates || 0} berichten`} />
              {webhookStatus.last_error && <ConfigRow label="Laatste fout" value={webhookStatus.last_error} status="error" />}
            </div>
          )}
        </div>
      </div>

      {/* Chat & Forms Stats */}
      <div className="admin-system-grid">
        <div className="admin-card">
          <h4><MessageSquare size={16} /> Live Chat</h4>
          <div className="admin-config-grid">
            <ConfigRow label="Berichten" value={stats.chat.total_messages} />
            <ConfigRow label="Sessies" value={stats.chat.total_sessions} />
          </div>
        </div>
        <div className="admin-card">
          <h4><FileText size={16} /> Formulieren</h4>
          <div className="admin-config-grid">
            <ConfigRow label="Consultatie aanvragen" value={stats.consultation_requests} />
            <ConfigRow label="Nieuwsbrief abonnees" value={stats.newsletter_subscribers} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SYSTEM PANEL
   ═══════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   STRIPE CONFIG CARD (inside System panel)
   ═══════════════════════════════════════════════════════════ */
function StripeConfigCard({ headers }) {
  const [cfg, setCfg] = useState(null);
  const [overview, setOverview] = useState(null);
  const [newKey, setNewKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [msg, setMsg] = useState('');

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const fetchCfg = async () => {
    const [cfgR, ovR] = await Promise.all([
      fetch(`${API}/admin/stripe/config`, { headers }).then(r => r.json()).catch(() => null),
      fetch(`${API}/admin/billing/overview`, { headers }).then(r => r.json()).catch(() => null),
    ]);
    if (cfgR) setCfg(cfgR);
    if (ovR) setOverview(ovR);
  };

  useEffect(() => { fetchCfg(); }, []);

  const saveKey = async () => {
    if (!newKey.startsWith('sk_')) { flash('Key moet beginnen met sk_test_ of sk_live_'); return; }
    const r = await fetch(`${API}/admin/stripe/config`, {
      method: 'PUT', headers, body: JSON.stringify({ api_key: newKey }),
    });
    const d = await r.json();
    if (r.ok) {
      flash(`Stripe key opgeslagen (${d.mode} modus)`);
      setNewKey('');
      setShowKey(false);
      fetchCfg();
    } else {
      flash(d.detail || 'Fout bij opslaan');
    }
  };

  if (!cfg) return null;

  const modeColor = cfg.mode === 'live' ? '#10b981' : cfg.mode === 'test' ? '#f59e0b' : '#ef4444';
  const modeLabel = cfg.mode === 'live' ? 'LIVE' : cfg.mode === 'test' ? 'TEST' : 'GEEN';

  return (
    <div className="admin-card" data-testid="stripe-config">
      {msg && <div className="admin-toast">{msg}</div>}
      <h4><CreditCard size={16} /> Stripe Configuratie</h4>

      <div className="admin-config-grid">
        <ConfigRow label="Status" value={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: modeColor, display: 'inline-block' }} />
            <strong style={{ color: modeColor }}>{modeLabel}</strong>
          </span>
        } />
        <ConfigRow label="Key" value={cfg.key_prefix} />
        <ConfigRow label="Bron" value={cfg.source === 'database' ? 'Database (Admin)' : 'Environment (.env)'} />
      </div>

      {overview && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem', margin: '0.75rem 0' }}>
          {[
            { label: 'Omzet', value: `€${overview.total_revenue?.toFixed(2)}`, color: '#10b981' },
            { label: 'Credits verkocht', value: overview.total_credits_sold?.toLocaleString(), color: '#6366f1' },
            { label: 'Betalende users', value: overview.total_paying_users, color: '#f59e0b' },
            { label: 'Transacties', value: overview.transactions_count, color: '#ec4899' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center', padding: '0.5rem', background: 'var(--card-bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: s.color }}>{s.value || 0}</div>
              <div style={{ fontSize: '0.68rem', opacity: 0.5 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
        <p className="admin-card-desc" style={{ marginBottom: '0.5rem' }}>
          {cfg.mode === 'test'
            ? 'Test modus actief. Vervang door je live key voor echte betalingen.'
            : cfg.mode === 'live'
              ? 'Live modus actief. Betalingen worden verwerkt.'
              : 'Geen Stripe key geconfigureerd.'}
        </p>
        {!showKey ? (
          <button className="admin-btn-sm" onClick={() => setShowKey(true)} data-testid="stripe-show-key-form">
            <KeyRound size={13} /> {cfg.has_key ? 'Key wijzigen' : 'Key instellen'}
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              className="admin-key-input"
              type="password"
              value={newKey}
              onChange={e => setNewKey(e.target.value)}
              placeholder="sk_live_... of sk_test_..."
              style={{ flex: 1 }}
              data-testid="stripe-key-input"
            />
            <button className="admin-btn" onClick={saveKey} data-testid="stripe-key-save">
              <Save size={14} /> Opslaan
            </button>
            <button className="admin-btn-sm" onClick={() => { setShowKey(false); setNewKey(''); }}>Annuleren</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SYSTEM / HEALTH PANEL
   ═══════════════════════════════════════════════════════════ */
function SystemPanel({ headers }) {
  const [health, setHealth] = useState(null);
  const fetchHealth = () => { fetch(`${API}/admin/system/health`, { headers }).then(r => r.json()).then(setHealth).catch(() => {}); };
  useEffect(() => { fetchHealth(); }, []);
  if (!health) return <Loading />;

  return (
    <div className="admin-system" data-testid="system-panel">
      <div className="admin-system-header">
        <h4><HeartPulse size={16} /> Status: <span className={health.status === 'healthy' ? 'text-green' : 'text-red'}>{health.status}</span></h4>
        <button className="admin-btn-sm" onClick={fetchHealth} data-testid="refresh-health"><RefreshCw size={14} /> Vernieuwen</button>
      </div>

      <div className="admin-system-grid">
        <div className="admin-card">
          <h4><Database size={16} /> Database</h4>
          <div className="admin-config-grid">
            <ConfigRow label="Verbinding" value={health.database.connected ? 'OK' : 'FOUT'} status={health.database.connected ? 'ok' : 'error'} />
            <ConfigRow label="Collections" value={health.database.collections} />
            <ConfigRow label="Objecten" value={health.database.objects?.toLocaleString()} />
            <ConfigRow label="Data" value={`${health.database.dataSize_mb} MB`} />
            <ConfigRow label="Opslag" value={`${health.database.storageSize_mb} MB`} />
          </div>
        </div>

        <div className="admin-card">
          <h4><Cpu size={16} /> LLM Configuratie</h4>
          <div className="admin-config-grid">
            <ConfigRow label="Provider" value={health.llm?.provider} />
            <ConfigRow label="Primair" value={health.llm?.primary} />
            <ConfigRow label="Fallback" value={`${health.llm?.fallback_provider || 'gemini'} / ${health.llm?.fallback || 'gemini-2.0-flash'}`} />
            <ConfigRow label="Key status" value={health.llm?.key_configured ? 'Geconfigureerd' : 'Ontbreekt'} status={health.llm?.key_configured ? 'ok' : 'error'} />
          </div>
        </div>
      </div>

      <div className="admin-system-grid">
        <div className="admin-card">
          <h4><MessageSquare size={16} /> Emma Avatar</h4>
          <div className="admin-config-grid">
            <ConfigRow label="Provider" value={health.emma?.provider || 'gemini'} />
            <ConfigRow label="Model" value={health.emma?.model || 'gemini-2.5-flash-lite'} />
          </div>
        </div>
        <div className="admin-card">
          <h4><Cpu size={16} /> Ollama (Lokaal)</h4>
          <div className="admin-config-grid">
            <ConfigRow label="Status" value={health.ollama?.status || 'offline'} status={health.ollama?.status === 'online' ? 'ok' : 'error'} />
            <ConfigRow label="URL" value={health.ollama?.url || 'N/A'} />
            <ConfigRow label="Model" value={health.ollama?.model || 'N/A'} />
            {health.ollama?.available_models?.length > 0 && (
              <ConfigRow label="Beschikbaar" value={(Array.isArray(health.ollama.available_models) ? health.ollama.available_models : []).join(', ')} status="ok" />
            )}
            {health.ollama?.model_available === false && health.ollama?.status === 'online' && (
              <ConfigRow label="Waarschuwing" value={`Model "${health.ollama.model}" niet gevonden`} status="error" />
            )}
          </div>
        </div>
      </div>

      <div className="admin-card">
        <h4>Collection Grootte</h4>
        <div className="admin-collections-grid">
          {Object.entries(health.collections || {}).map(([name, count]) => (
            <div key={name} className="admin-collection-item">
              <span className="admin-config-label">{name}</span>
              <span className="admin-collection-count">{count >= 0 ? count.toLocaleString() : 'N/A'}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-card">
        <h4>Systeem Info</h4>
        <div className="admin-config-grid">
          <ConfigRow label="Backend versie" value={health.backend_version} />
          <ConfigRow label="Python" value={health.python_version} />
          <ConfigRow label="Uptime" value={health.uptime} />
        </div>
      </div>

      {(health.recent_ai_errors || []).length > 0 && (
        <div className="admin-card">
          <h4><AlertTriangle size={15} /> Recente AI Errors</h4>
          {(Array.isArray(health.recent_ai_errors) ? health.recent_ai_errors : []).map((e, i) => (
            <div key={i} className="admin-error-item">
              <span className="badge badge-inactive">{e.status_code || 'ERR'}</span>
              <span className="admin-error-path">{e.path || e.message}</span>
              <span className="admin-notif-time">{e.timestamp ? new Date(e.timestamp).toLocaleString('nl-NL') : ''}</span>
            </div>
          ))}
        </div>
      )}

      {/* Stripe & Billing */}
      <StripeConfigCard headers={headers} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SHARED
   ═══════════════════════════════════════════════════════════ */
function ConfigRow({ label, value, status }) {
  return (
    <div className="admin-config-row">
      <span className="admin-config-label">{label}</span>
      <span className={`admin-config-value ${status || ''}`}>{value}</span>
    </div>
  );
}

function Loading() {
  return <div className="admin-loading"><RefreshCw size={20} className="spin" /> Laden...</div>;
}

/* ── Newsletter Panel ────────────────────────────────────────────── */
function NewsletterPanel({ headers }) {
  const [newsletters, setNewsletters] = useState([]);
  const [config, setConfig] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(null);
  const [preview, setPreview] = useState(null);
  const [flashMsg, setFlashMsg] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [showSubs, setShowSubs] = useState(false);

  const flash = (msg) => { setFlashMsg(msg); setTimeout(() => setFlashMsg(''), 4000); };

  const fetchData = async () => {
    const safeFetch = async (url) => {
      try { const r = await fetch(url, { headers }); return r.ok ? await r.json() : null; }
      catch { return null; }
    };
    const [nlData, cfgData, brandData, subData] = await Promise.all([
      safeFetch(`${API}/admin/newsletter/list`),
      safeFetch(`${API}/admin/newsletter/config`),
      safeFetch(`${API}/admin/branding`),
      safeFetch(`${API}/admin/newsletter/subscribers`),
    ]);
    if (nlData) setNewsletters(nlData.newsletters || []);
    if (cfgData) setConfig(cfgData);
    if (brandData) setLogoUrl(brandData.newsletter_logo_url || '');
    if (subData) setSubscribers(subData.subscribers || []);
  };
  useEffect(() => { fetchData(); }, []);

  const generateNewsletter = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/admin/newsletter/generate`, { method: 'POST', headers });
      const data = await res.json();
      if (res.ok) {
        flash(`Edition #${data.edition} gegenereerd!`);
        fetchData();
      } else {
        flash(`Fout: ${data.detail || 'Genereren mislukt'}`);
      }
    } catch (e) { flash('Fout bij genereren'); }
    setGenerating(false);
  };

  const sendNewsletter = async (edition) => {
    if (!window.confirm(`Weet je zeker dat je Edition #${edition} wilt versturen naar alle ${config?.subscribers_count || 0} abonnees?`)) return;
    setSending(edition);
    try {
      const res = await fetch(`${API}/admin/newsletter/${edition}/send`, { method: 'POST', headers });
      const data = await res.json();
      if (res.ok) {
        flash(`Verzonden naar ${data.recipients} abonnee(s)!`);
        fetchData();
      } else {
        flash(`Fout: ${data.detail || data.error || 'Verzenden mislukt'}`);
      }
    } catch (e) { flash('Fout bij verzenden'); }
    setSending(null);
  };

  const openPreview = async (edition) => {
    try {
      const res = await fetch(`${API}/admin/newsletter/${edition}/preview`, { headers });
      const data = await res.json();
      setPreview(data);
    } catch (e) { flash('Preview laden mislukt'); }
  };

  const toggleAutoSend = async () => {
    const newVal = !config?.auto_send;
    await fetch(`${API}/admin/newsletter/config`, {
      method: 'PUT', headers,
      body: JSON.stringify({ auto_send: newVal }),
    });
    flash(newVal ? 'Automatisch verzenden ingeschakeld' : 'Automatisch verzenden uitgeschakeld');
    fetchData();
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/admin/branding/logo`, {
        method: 'POST',
        headers: { Authorization: headers.Authorization },
        body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        setLogoUrl(data.url);
        flash('Logo geüpload! Wordt gebruikt in nieuwe nieuwsbrieven.');
      } else {
        flash(`Fout: ${data.detail || 'Upload mislukt'}`);
      }
    } catch (err) { flash('Upload mislukt'); }
    setUploading(false);
    e.target.value = '';
  };

  const deleteSubscriber = async (email) => {
    if (!window.confirm(`Abonnee "${email}" verwijderen?`)) return;
    try {
      const res = await fetch(`${API}/admin/newsletter/subscribers/${encodeURIComponent(email)}`, {
        method: 'DELETE', headers,
      });
      if (res.ok) { flash(`${email} verwijderd`); fetchData(); }
      else flash('Verwijderen mislukt');
    } catch { flash('Verwijderen mislukt'); }
  };

  return (
    <div className="admin-system" data-testid="newsletter-panel">
      <div className="admin-system-header">
        <h4><Newspaper size={16} /> AI Nieuwsbrief Beheer</h4>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {flashMsg && <span style={{ color: '#10b981', fontSize: '0.8rem', fontWeight: 500 }}>{flashMsg}</span>}
          <button className="admin-btn-sm" onClick={fetchData}><RefreshCw size={14} /> Vernieuwen</button>
        </div>
      </div>

      {/* Config & Actions */}
      <div className="admin-system-grid">
        <div className="admin-card">
          <h4><Send size={16} /> Instellingen</h4>
          <div className="admin-config-grid">
            <ConfigRow label="Schema" value="Maandag 07:00 CET" />
            <ConfigRow label="Taal" value="Engels" />
            <ConfigRow label="Abonnees" value={subscribers.length} status="ok" />
          </div>

          {/* Logo upload */}
          <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <span style={{ fontSize: '0.8rem', color: '#BCA182', fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Newsletter Logo</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {logoUrl ? (
                <img src={logoUrl.startsWith('/') ? `${((API) == null ? '' : String(API)).replace('/api', '')}${logoUrl}` : logoUrl} alt="Logo" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', background: '#232323' }} />
              ) : (
                <div style={{ width: 40, height: 40, borderRadius: 8, background: '#1E1914', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#F4F7FB', fontSize: 18 }}>H</div>
              )}
              <label className="admin-btn-sm" style={{ cursor: 'pointer', position: 'relative' }} data-testid="upload-logo-btn">
                <Upload size={13} /> {uploading ? 'Uploaden...' : 'Logo Uploaden'}
                <input type="file" accept="image/*" onChange={uploadLogo} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
              </label>
            </div>
          </div>

          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem' }} data-testid="auto-send-toggle">
              <div
                onClick={toggleAutoSend}
                style={{
                  width: '44px', height: '24px', borderRadius: '12px', position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                  background: config?.auto_send ? '#081D14' : '#555',
                }}
              >
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%', background: '#0E1620', position: 'absolute', top: '2px',
                  left: config?.auto_send ? '22px' : '2px', transition: 'left 0.2s',
                }} />
              </div>
              Automatisch verzenden
            </label>
          </div>
        </div>

        <div className="admin-card">
          <h4><FileText size={16} /> Genereren</h4>
          <p style={{ fontSize: '0.8rem', color: '#999', margin: '0 0 0.75rem' }}>
            Genereer een nieuwe AI-nieuwsbrief met de top 5 AI weetjes van deze week. Je kunt deze bekijken en goedkeuren voordat je verstuurt.
          </p>
          <button
            className="admin-btn-sm accent"
            onClick={generateNewsletter}
            disabled={generating}
            data-testid="generate-newsletter-btn"
            style={{ padding: '0.5rem 1rem' }}
          >
            {generating ? <><RefreshCw size={14} className="spin" /> Genereren...</> : <><Newspaper size={14} /> Nieuwe Nieuwsbrief Genereren</>}
          </button>
        </div>
      </div>

      {/* Subscribers */}
      <div className="admin-card" style={{ marginTop: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showSubs ? '0.75rem' : 0 }}>
          <h4 style={{ margin: 0, cursor: 'pointer' }} onClick={() => setShowSubs(!showSubs)}>
            <Users size={16} /> Abonnees ({subscribers.length})
            <span style={{ fontSize: '0.7rem', color: '#999', marginLeft: '0.5rem' }}>{showSubs ? '▲' : '▼'}</span>
          </h4>
        </div>
        {showSubs && (
          subscribers.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: '#999' }}>Geen abonnees.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ textAlign: 'left', padding: '0.4rem', color: '#BCA182' }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '0.4rem', color: '#BCA182' }}>Naam</th>
                  <th style={{ textAlign: 'left', padding: '0.4rem', color: '#BCA182' }}>Datum</th>
                  <th style={{ textAlign: 'right', padding: '0.4rem', color: '#BCA182' }}></th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(subscribers) ? subscribers : []).map(sub => (
                  <tr key={sub.email} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ padding: '0.4rem' }}>{sub.email}</td>
                    <td style={{ padding: '0.4rem', color: '#999' }}>{sub.name || '-'}</td>
                    <td style={{ padding: '0.4rem', color: '#999' }}>
                      {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                    </td>
                    <td style={{ padding: '0.4rem', textAlign: 'right' }}>
                      <button className="admin-btn-sm" onClick={() => deleteSubscriber(sub.email)} style={{ color: '#ef4444' }} data-testid={`delete-sub-${sub.email}`}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* Newsletter List */}
      <div className="admin-card" style={{ marginTop: '1rem' }}>
        <h4 style={{ marginBottom: '0.75rem' }}><Mail size={16} /> Edities ({newsletters.length})</h4>
        {newsletters.length === 0 ? (
          <p style={{ fontSize: '0.85rem', color: '#999' }}>Nog geen nieuwsbrieven gegenereerd.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem', color: '#BCA182' }}>#</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', color: '#BCA182' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', color: '#BCA182' }}>Aangemaakt</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', color: '#BCA182' }}>Verzonden</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', color: '#BCA182' }}>Ontvangers</th>
                <th style={{ textAlign: 'right', padding: '0.5rem', color: '#BCA182' }}>Acties</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(newsletters) ? newsletters : []).map(nl => (
                <tr key={nl.edition} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td style={{ padding: '0.5rem', fontWeight: 600 }}>#{nl.edition}</td>
                  <td style={{ padding: '0.5rem' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600,
                      background: nl.status === 'sent' ? 'rgba(16,185,129,0.15)' : 'rgba(234,179,8,0.15)',
                      color: nl.status === 'sent' ? '#10b981' : '#eab308',
                    }}>
                      {nl.status === 'sent' ? 'Verzonden' : 'Concept'}
                    </span>
                  </td>
                  <td style={{ padding: '0.5rem', color: '#999' }}>
                    {nl.created_at ? new Date(nl.created_at).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </td>
                  <td style={{ padding: '0.5rem', color: '#999' }}>
                    {nl.sent_at ? new Date(nl.sent_at).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </td>
                  <td style={{ padding: '0.5rem' }}>{nl.recipients_count || '-'}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                    <button className="admin-btn-sm" onClick={() => openPreview(nl.edition)} data-testid={`preview-nl-${nl.edition}`}>
                      <Eye size={13} /> Preview
                    </button>
                    {nl.status !== 'sent' && (
                      <button
                        className="admin-btn-sm accent"
                        onClick={() => sendNewsletter(nl.edition)}
                        disabled={sending === nl.edition}
                        data-testid={`send-nl-${nl.edition}`}
                      >
                        {sending === nl.edition ? <><RefreshCw size={13} className="spin" /> ...</> : <><Send size={13} /> Versturen</>}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Preview Modal */}
      {preview && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem',
        }} onClick={() => setPreview(null)}>
          <div
            style={{
              background: '#0E1620', borderRadius: '16px', maxWidth: '660px', width: '100%', maxHeight: '85vh',
              overflow: 'auto', position: 'relative',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ position: 'sticky', top: 0, background: '#232323', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '16px 16px 0 0' }}>
              <span style={{ color: '#BCA182', fontWeight: 600, fontSize: '0.9rem' }}>Preview - Edition #{preview.edition}</span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {preview.status !== 'sent' && (
                  <button className="admin-btn-sm accent" onClick={() => { sendNewsletter(preview.edition); setPreview(null); }}>
                    <Send size={13} /> Goedkeuren & Versturen
                  </button>
                )}
                <button className="admin-btn-sm" onClick={() => setPreview(null)}>Sluiten</button>
              </div>
            </div>
            <div dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   CONTEXT GATEWAY PANEL
   ═══════════════════════════════════════════════════════════ */
function GatewayPanel({ headers }) {
  const [subTab, setSubTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [policies, setPolicies] = useState([]);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testProvider, setTestProvider] = useState('openai');
  const [loading, setLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState({ direction: '', sensitivity: '', blocked: false });

  // Custom PII Patterns state
  const [patterns, setPatterns] = useState([]);
  const [patternsLoading, setPatternsLoading] = useState(false);
  const [showPatternForm, setShowPatternForm] = useState(false);
  const [patternForm, setPatternForm] = useState({
    name: '', label: '', regex: '', sensitivity: 'confidential', action: 'mask', description: ''
  });
  const [patternError, setPatternError] = useState('');
  const [patternMsg, setPatternMsg] = useState('');
  const [deletingPattern, setDeletingPattern] = useState(null);
  const [testingPattern, setTestingPattern] = useState(null);
  const [patternTestInput, setPatternTestInput] = useState('');
  const [patternTestResult, setPatternTestResult] = useState(null);

  const testPatternLocally = (regex, action, name) => {
    if (!((patternTestInput) == null ? '' : String(patternTestInput)).trim()) { setPatternTestResult(null); return; }
    try {
      const re = new RegExp(regex, 'g');
      const matches = [];
      let m;
      while ((m = re.exec(patternTestInput)) !== null) { matches.push({ value: m[0], start: m.index, end: m.index + m[0].length }); }
      let sanitized = patternTestInput;
      for (let i = matches.length - 1; i >= 0; i--) {
        const mat = matches[i];
        let replacement;
        if (action === 'redact') replacement = `[${((name) == null ? '' : String(name)).toUpperCase()}_REDACTED]`;
        else if (action === 'mask') replacement = `[${((name) == null ? '' : String(name)).toUpperCase()}-***${mat.value.slice(-3)}]`;
        else if (action === 'pseudonym') replacement = `[${((name) == null ? '' : String(name)).toUpperCase()}-xxxx]`;
        else replacement = mat.value;
        sanitized = sanitized.slice(0, mat.start) + replacement + sanitized.slice(mat.end);
      }
      setPatternTestResult({ matches: matches.length, sanitized });
    } catch { setPatternTestResult({ matches: 0, sanitized: patternTestInput, error: 'Regex fout' }); }
  };

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/gateway/stats?hours=24`, { headers });
      const data = await res.json();
      setStats(data);
    } catch {}
  }, []);

  const fetchAudit = useCallback(async () => {
    const params = new URLSearchParams({ limit: '50' });
    if (auditFilter.direction) params.set('direction', auditFilter.direction);
    if (auditFilter.sensitivity) params.set('sensitivity', auditFilter.sensitivity);
    if (auditFilter.blocked) params.set('blocked_only', 'true');
    try {
      const res = await fetch(`${API}/gateway/audit-log?${params}`, { headers });
      const data = await res.json();
      setAuditLog(data.events || []);
      setAuditTotal(data.total || 0);
    } catch {}
  }, [auditFilter]);

  const fetchPolicies = useCallback(async () => {
    try {
      const res = await fetch(`${API}/gateway/policies`, { headers });
      const data = await res.json();
      setPolicies(data.policies || []);
    } catch {}
  }, []);

  const fetchPatterns = useCallback(async () => {
    setPatternsLoading(true);
    try {
      const res = await fetch(`${API}/gateway/patterns`, { headers });
      const data = await res.json();
      setPatterns(data.patterns || []);
    } catch {}
    setPatternsLoading(false);
  }, []);

  useEffect(() => { fetchStats(); fetchAudit(); fetchPolicies(); fetchPatterns(); }, []);
  useEffect(() => { fetchAudit(); }, [auditFilter]);

  const runTestScan = async () => {
    if (!((testText) == null ? '' : String(testText)).trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/gateway/test-scan`, {
        method: 'POST', headers,
        body: JSON.stringify({ text: testText, provider: testProvider }),
      });
      setTestResult(await res.json());
    } catch {}
    setLoading(false);
  };

  const createPattern = async () => {
    setPatternError('');
    if (!((patternForm.name) == null ? '' : String(patternForm.name)).trim() || !((patternForm.label) == null ? '' : String(patternForm.label)).trim() || !((patternForm.regex) == null ? '' : String(patternForm.regex)).trim()) {
      setPatternError('Naam, label en regex zijn verplicht');
      return;
    }
    try { new RegExp(patternForm.regex); } catch { setPatternError('Ongeldige regex syntax'); return; }
    setPatternsLoading(true);
    try {
      const res = await fetch(`${API}/gateway/patterns`, {
        method: 'POST', headers, body: JSON.stringify(patternForm),
      });
      const data = await res.json();
      if (!res.ok) { setPatternError(data.detail || 'Fout bij aanmaken'); setPatternsLoading(false); return; }
      setPatternMsg(`Patroon "${patternForm.name}" aangemaakt`);
      setPatternForm({ name: '', label: '', regex: '', sensitivity: 'confidential', action: 'mask', description: '' });
      setShowPatternForm(false);
      fetchPatterns();
      setTimeout(() => setPatternMsg(''), 3000);
    } catch (e) { setPatternError('Netwerkfout'); }
    setPatternsLoading(false);
  };

  const deletePattern = async (patternId, patternName) => {
    setPatternsLoading(true);
    try {
      const res = await fetch(`${API}/gateway/patterns/${patternId}`, { method: 'DELETE', headers });
      if (res.ok) {
        setPatternMsg(`Patroon "${patternName}" verwijderd`);
        setDeletingPattern(null);
        fetchPatterns();
        setTimeout(() => setPatternMsg(''), 3000);
      }
    } catch {}
    setPatternsLoading(false);
  };

  const SUB_TABS = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'audit', label: 'Audit Log' },
    { id: 'patterns', label: 'PII Patronen' },
    { id: 'policies', label: 'Policies' },
    { id: 'test', label: 'Test Console' },
  ];

  const severityColor = (s) => ({
    public: '#16A34A', internal: '#3B82F6', confidential: '#D97706', restricted: '#DC2626',
  }[s] || '#6B7280');

  return (
    <div data-testid="gateway-panel">
      {/* Sub-tab Navigation */}
      <div className="gw-subtabs" data-testid="gw-subtabs">
        {(Array.isArray(SUB_TABS) ? SUB_TABS : []).map(t => (
          <button key={t.id} data-testid={`gw-subtab-${t.id}`}
            className={`gw-subtab ${subTab === t.id ? 'active' : ''}`}
            onClick={() => setSubTab(t.id)}>{t.label}</button>
        ))}
        <button className="admin-btn-sm" style={{ marginLeft: 'auto' }} onClick={() => { fetchStats(); fetchAudit(); }}>
          <RefreshCw size={13} /> Vernieuw
        </button>
      </div>

      {/* ── DASHBOARD ── */}
      {subTab === 'dashboard' && (
        <div className="gw-dashboard" data-testid="gw-dashboard">
          {!stats ? <p style={{ color: 'var(--text-muted)' }}>Laden...</p> : (
            <>
              <div className="gw-stat-grid">
                <div className="gw-stat-card" data-testid="gw-stat-total">
                  <div className="gw-stat-value">{stats.total_requests}</div>
                  <div className="gw-stat-label">Totaal Scans (24u)</div>
                </div>
                <div className="gw-stat-card warn" data-testid="gw-stat-blocked">
                  <div className="gw-stat-value">{stats.blocked_requests}</div>
                  <div className="gw-stat-label">Geblokkeerd</div>
                </div>
                <div className="gw-stat-card info" data-testid="gw-stat-leaks">
                  <div className="gw-stat-value">{stats.outbound_leaks_caught}</div>
                  <div className="gw-stat-label">Lekken Gevangen</div>
                </div>
                <div className="gw-stat-card" data-testid="gw-stat-downgrades">
                  <div className="gw-stat-value">{stats.tier_downgrades}</div>
                  <div className="gw-stat-label">Tier Downgrades</div>
                </div>
              </div>

              <div className="gw-breakdown-row">
                {/* Sensitivity Breakdown */}
                <div className="gw-breakdown-card" data-testid="gw-sensitivity-breakdown">
                  <h4>Sensitivity Levels</h4>
                  <div className="gw-bar-list">
                    {['public', 'internal', 'confidential', 'restricted'].map(level => {
                      const count = stats.sensitivity_breakdown[level] || 0;
                      const pct = stats.total_requests > 0 ? (count / stats.total_requests * 100) : 0;
                      return (
                        <div key={level} className="gw-bar-item">
                          <div className="gw-bar-header">
                            <span className="gw-bar-dot" style={{ background: severityColor(level) }} />
                            <span className="gw-bar-name">{level}</span>
                            <span className="gw-bar-count">{count}</span>
                          </div>
                          <div className="gw-bar-track">
                            <div className="gw-bar-fill" style={{ width: `${pct}%`, background: severityColor(level) }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Detection Types */}
                <div className="gw-breakdown-card" data-testid="gw-detection-types">
                  <h4>PII Detecties</h4>
                  {Object.keys(stats.detection_types).length === 0 ? (
                    <p className="gw-muted">Geen detecties in afgelopen 24u</p>
                  ) : (
                    <div className="gw-tag-list">
                      {Object.entries(stats.detection_types).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                        <span key={type} className="gw-tag">{type} <strong>{count}</strong></span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Model Tier */}
                <div className="gw-breakdown-card" data-testid="gw-tier-breakdown">
                  <h4>Model Tier Routing</h4>
                  <div className="gw-tag-list">
                    {Object.entries(stats.tier_breakdown).map(([tier, count]) => (
                      <span key={tier} className={`gw-tag tier-${tier}`}>{tier} <strong>{count}</strong></span>
                    ))}
                    {Object.keys(stats.tier_breakdown).length === 0 && (
                      <p className="gw-muted">Nog geen routing data</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── AUDIT LOG ── */}
      {subTab === 'audit' && (
        <div className="gw-audit" data-testid="gw-audit">
          <div className="gw-audit-filters">
            <select value={auditFilter.direction} onChange={e => setAuditFilter(p => ({...p, direction: e.target.value}))}>
              <option value="">Alle richtingen</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
            <select value={auditFilter.sensitivity} onChange={e => setAuditFilter(p => ({...p, sensitivity: e.target.value}))}>
              <option value="">Alle niveaus</option>
              <option value="public">Public</option>
              <option value="internal">Internal</option>
              <option value="confidential">Confidential</option>
              <option value="restricted">Restricted</option>
            </select>
            <label className="gw-checkbox-label">
              <input type="checkbox" checked={auditFilter.blocked} onChange={e => setAuditFilter(p => ({...p, blocked: e.target.checked}))} />
              Alleen geblokkeerd
            </label>
            <span className="gw-muted" style={{ marginLeft: 'auto' }}>{auditTotal} events</span>
          </div>

          <div className="gw-audit-table">
            <div className="gw-audit-header-row">
              <span>Tijd</span><span>Richting</span><span>Sensitivity</span><span>Detecties</span><span>Tier</span><span>Status</span>
            </div>
            {auditLog.length === 0 ? (
              <div className="gw-empty">Geen audit events gevonden</div>
            ) : (
              (Array.isArray(auditLog) ? auditLog : []).map((evt, i) => (
                <div key={evt.event_id || i} className="gw-audit-row" data-testid={`gw-audit-${i}`}>
                  <span className="gw-audit-time">{evt.timestamp ? new Date(evt.timestamp).toLocaleTimeString('nl-NL') : '-'}</span>
                  <span className={`gw-audit-dir ${evt.direction}`}>{evt.direction === 'inbound' ? 'IN' : 'OUT'}</span>
                  <span className="gw-audit-sens">
                    <span className="gw-dot" style={{ background: severityColor(evt.sensitivity) }} />
                    {evt.sensitivity}
                  </span>
                  <span>{evt.detections_count || 0} {evt.detection_types?.length > 0 && <small>({(Array.isArray(evt.detection_types) ? evt.detection_types : []).join(', ')})</small>}</span>
                  <span className={`gw-tier-badge ${evt.model_tier || ''}`}>
                    {evt.model_tier || '-'}{evt.tier_downgraded && ' (downgraded)'}
                  </span>
                  <span>{evt.blocked ? <XCircle size={14} style={{ color: '#DC2626' }} /> : <CheckCircle2 size={14} style={{ color: '#16A34A' }} />}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── PII PATRONEN ── */}
      {subTab === 'patterns' && (
        <div className="gw-patterns" data-testid="gw-patterns-panel">
          {patternMsg && <div className="admin-toast">{patternMsg}</div>}

          <div className="gw-patterns-header">
            <div>
              <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', margin: 0 }}>
                <Shield size={16} /> Custom PII Patronen
              </h4>
              <p className="gw-muted" style={{ margin: '0.25rem 0 0' }}>
                Definieer regex-patronen voor extra PII/secret detectie. Actieve patronen worden automatisch meegenomen in elke gateway scan.
              </p>
            </div>
            <button className="admin-btn-sm accent" onClick={() => { setShowPatternForm(!showPatternForm); setPatternError(''); }} data-testid="gw-patterns-add-btn">
              {showPatternForm ? <><XCircle size={13} /> Annuleren</> : <><Plus size={13} /> Nieuw Patroon</>}
            </button>
          </div>

          {/* Add Pattern Form */}
          {showPatternForm && (
            <div className="gw-pattern-form" data-testid="gw-pattern-form">
              {patternError && <div className="gw-pattern-error" data-testid="gw-pattern-error"><AlertTriangle size={13} /> {patternError}</div>}
              <div className="gw-pattern-form-grid">
                <div className="gw-pattern-field">
                  <label>Naam (uniek, snake_case)</label>
                  <input data-testid="gw-pattern-name" placeholder="bijv. btw_nummer" value={patternForm.name}
                    onChange={e => setPatternForm(p => ({...p, name: ((e.target.value) == null ? '' : String(e.target.value)).toLowerCase().replace(/[^a-z0-9_]/g, '_')}))} />
                </div>
                <div className="gw-pattern-field">
                  <label>Label (weergavenaam)</label>
                  <input data-testid="gw-pattern-label" placeholder="bijv. BTW Nummer" value={patternForm.label}
                    onChange={e => setPatternForm(p => ({...p, label: e.target.value}))} />
                </div>
                <div className="gw-pattern-field" style={{ gridColumn: '1 / -1' }}>
                  <label>Regex Patroon</label>
                  <input data-testid="gw-pattern-regex" placeholder="bijv. \bNL\d{9}B\d{2}\b" value={patternForm.regex}
                    onChange={e => setPatternForm(p => ({...p, regex: e.target.value}))} className="gw-pattern-regex-input" />
                </div>
                <div className="gw-pattern-field">
                  <label>Gevoeligheid</label>
                  <select data-testid="gw-pattern-sensitivity" value={patternForm.sensitivity}
                    onChange={e => setPatternForm(p => ({...p, sensitivity: e.target.value}))}>
                    <option value="public">Public</option>
                    <option value="internal">Internal</option>
                    <option value="confidential">Confidential</option>
                    <option value="restricted">Restricted</option>
                  </select>
                </div>
                <div className="gw-pattern-field">
                  <label>Actie</label>
                  <select data-testid="gw-pattern-action" value={patternForm.action}
                    onChange={e => setPatternForm(p => ({...p, action: e.target.value}))}>
                    <option value="redact">Redact (verwijderen)</option>
                    <option value="mask">Mask (deels maskeren)</option>
                    <option value="pseudonym">Pseudonimiseren</option>
                    <option value="pass">Doorlaten</option>
                  </select>
                </div>
                <div className="gw-pattern-field" style={{ gridColumn: '1 / -1' }}>
                  <label>Beschrijving (optioneel)</label>
                  <input data-testid="gw-pattern-desc" placeholder="Korte omschrijving van het patroon" value={patternForm.description}
                    onChange={e => setPatternForm(p => ({...p, description: e.target.value}))} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                <button className="admin-btn-sm accent" onClick={createPattern} disabled={patternsLoading} data-testid="gw-pattern-submit">
                  {patternsLoading ? <RefreshCw size={13} className="spin" /> : <><CheckCircle2 size={13} /> Patroon Opslaan</>}
                </button>
              </div>
            </div>
          )}

          {/* Built-in Patterns Info */}
          <div className="gw-builtin-info" data-testid="gw-builtin-patterns">
            <h5 style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <Lock size={13} /> Ingebouwde Patronen
            </h5>
            <div className="gw-builtin-tags">
              {[
                { name: 'BSN', sens: 'restricted' },
                { name: 'IBAN', sens: 'confidential' },
                { name: 'Creditcard', sens: 'restricted' },
                { name: 'KvK', sens: 'confidential' },
                { name: 'E-mail', sens: 'internal' },
                { name: 'Telefoon NL', sens: 'confidential' },
                { name: 'Telefoon Intl', sens: 'confidential' },
                { name: 'API Key', sens: 'restricted' },
                { name: 'Wachtwoord', sens: 'restricted' },
                { name: 'IP-adres', sens: 'internal' },
              ].map(b => (
                <span key={b.name} className="gw-builtin-tag" data-testid={`gw-builtin-${b.name}`}>
                  <span className="gw-dot" style={{ background: severityColor(b.sens) }} />
                  {b.name}
                </span>
              ))}
            </div>
          </div>

          {/* Custom Patterns List */}
          <div className="gw-custom-patterns-list" data-testid="gw-custom-patterns-list">
            <h5 style={{ margin: '0 0 0.5rem', fontSize: '0.8rem' }}>
              Custom Patronen ({patterns.length})
              {patternsLoading && <RefreshCw size={11} className="spin" style={{ marginLeft: '0.4rem' }} />}
            </h5>
            {patterns.length === 0 ? (
              <div className="gw-empty" style={{ padding: '1.5rem' }}>
                <p>Geen custom patronen gedefinieerd. Klik op "Nieuw Patroon" om er een toe te voegen.</p>
              </div>
            ) : (
              <div className="gw-patterns-table">
                <div className="gw-patterns-table-header">
                  <span>Naam</span>
                  <span>Regex</span>
                  <span>Gevoeligheid</span>
                  <span>Actie</span>
                  <span>Status</span>
                  <span></span>
                </div>
                {(Array.isArray(patterns) ? patterns : []).map(p => (
                  <div key={p.pattern_id} data-testid={`gw-pattern-${p.pattern_id}`}>
                    <div className="gw-patterns-table-row">
                      <div className="gw-pattern-cell-name">
                        <strong>{p.label || p.name}</strong>
                        {p.description && <span className="gw-pattern-desc">{p.description}</span>}
                      </div>
                      <div className="gw-pattern-cell-regex">
                        <code>{p.regex}</code>
                      </div>
                      <div>
                        <span className="gw-sens-badge" style={{ background: severityColor(p.sensitivity) + '22', color: severityColor(p.sensitivity) }}>
                          {p.sensitivity}
                        </span>
                      </div>
                      <div>
                        <span className="gw-action-badge">{p.action}</span>
                      </div>
                      <div>
                        <span className={`gw-status-badge ${p.active ? 'active' : ''}`}>
                          {p.active ? 'Actief' : 'Inactief'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button className="admin-btn-sm" style={{ color: '#D97706' }}
                          onClick={() => { setTestingPattern(testingPattern === p.pattern_id ? null : p.pattern_id); setPatternTestInput(''); setPatternTestResult(null); }}
                          data-testid={`gw-pattern-test-${p.pattern_id}`} title="Test patroon">
                          <Zap size={13} />
                        </button>
                        {deletingPattern === p.pattern_id ? (
                          <div style={{ display: 'flex', gap: '0.2rem' }}>
                            <button className="admin-btn-sm danger" onClick={() => deletePattern(p.pattern_id, p.name)} data-testid={`gw-pattern-confirm-delete-${p.pattern_id}`}>Ja</button>
                            <button className="admin-btn-sm" onClick={() => setDeletingPattern(null)}>Nee</button>
                          </div>
                        ) : (
                          <button className="admin-btn-sm" style={{ color: '#ef4444' }} onClick={() => setDeletingPattern(p.pattern_id)} data-testid={`gw-pattern-delete-${p.pattern_id}`}>
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                    {testingPattern === p.pattern_id && (
                      <div className="gw-pattern-test-inline" data-testid={`gw-pattern-test-panel-${p.pattern_id}`}>
                        <div className="gw-pattern-test-row">
                          <input
                            data-testid={`gw-pattern-test-input-${p.pattern_id}`}
                            className="gw-pattern-test-input"
                            placeholder={`Plak tekst om "${p.label || p.name}" te testen...`}
                            value={patternTestInput}
                            onChange={e => setPatternTestInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && testPatternLocally(p.regex, p.action, p.name)}
                          />
                          <button className="admin-btn-sm accent" onClick={() => testPatternLocally(p.regex, p.action, p.name)}
                            data-testid={`gw-pattern-test-run-${p.pattern_id}`}>
                            <Eye size={13} /> Test
                          </button>
                        </div>
                        {patternTestResult && (
                          <div className="gw-pattern-test-result" data-testid={`gw-pattern-test-result-${p.pattern_id}`}>
                            <div className="gw-pattern-test-meta">
                              <span className={`gw-pattern-match-count ${patternTestResult.matches > 0 ? 'found' : ''}`}>
                                {patternTestResult.matches > 0 ? <><CheckCircle2 size={12} /> {patternTestResult.matches} match{patternTestResult.matches !== 1 ? 'es' : ''}</> : <><XCircle size={12} /> Geen matches</>}
                              </span>
                              {patternTestResult.error && <span style={{ color: '#ef4444', fontSize: '0.72rem' }}>{patternTestResult.error}</span>}
                            </div>
                            {patternTestResult.matches > 0 && (
                              <div className="gw-pattern-test-output">
                                <span className="gw-pattern-test-label">Gesanitiseerd:</span>
                                <code data-testid={`gw-pattern-test-sanitized-${p.pattern_id}`}>{patternTestResult.sanitized}</code>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── POLICIES ── */}
      {subTab === 'policies' && (
        <div className="gw-policies" data-testid="gw-policies">
          <div className="gw-policies-info">
            <Shield size={18} style={{ color: '#D97706' }} />
            <p>Tenant-specifieke policies bepalen welke data naar welke provider mag. Standaard worden <strong>restricted</strong> data alleen lokaal verwerkt.</p>
          </div>
          {policies.length === 0 ? (
            <div className="gw-empty">
              <p>Geen custom policies — standaard beveiligingsregels zijn actief:</p>
              <ul className="gw-default-rules">
                <li><span className="gw-dot" style={{ background: '#16A34A' }} /> <strong>Public</strong> → Alle providers (local, private, external)</li>
                <li><span className="gw-dot" style={{ background: '#3B82F6' }} /> <strong>Internal</strong> → Alle providers</li>
                <li><span className="gw-dot" style={{ background: '#D97706' }} /> <strong>Confidential</strong> → Alleen local + private</li>
                <li><span className="gw-dot" style={{ background: '#DC2626' }} /> <strong>Restricted</strong> → Alleen local</li>
              </ul>
            </div>
          ) : (
            <div className="gw-policy-list">
              {(Array.isArray(policies) ? policies : []).map(p => (
                <div key={p.tenant_id} className="gw-policy-card" data-testid={`gw-policy-${p.tenant_id}`}>
                  <div className="gw-policy-header">
                    <strong>{p.name || p.tenant_id}</strong>
                    <span className={`gw-status-badge ${p.active ? 'active' : ''}`}>{p.active ? 'Actief' : 'Inactief'}</span>
                  </div>
                  <div className="gw-policy-details">
                    {p.blocked_sensitivity_levels?.length > 0 && (
                      <div>Geblokkeerde niveaus: {(Array.isArray(p.blocked_sensitivity_levels) ? p.blocked_sensitivity_levels : []).join(', ')}</div>
                    )}
                    {p.allowed_external_providers?.length > 0 && (
                      <div>Externe providers: {(Array.isArray(p.allowed_external_providers) ? p.allowed_external_providers : []).join(', ')}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TEST CONSOLE ── */}
      {subTab === 'test' && (
        <div className="gw-test" data-testid="gw-test-console">
          <div className="gw-test-header">
            <h4>Gateway Test Console</h4>
            <p className="gw-muted">Test de gateway scanner — plak tekst en bekijk wat er gedetecteerd en gesanitiseerd wordt.</p>
          </div>

          <div className="gw-test-input-area">
            <textarea
              data-testid="gw-test-input"
              className="gw-test-textarea"
              placeholder="Plak hier tekst met PII, secrets, of gevoelige data om te testen...&#10;&#10;Voorbeeld: Mijn BSN is 123456782 en mijn IBAN is NL91ABNA0417164300"
              value={testText}
              onChange={e => setTestText(e.target.value)}
              rows={5}
            />
            <div className="gw-test-controls">
              <select data-testid="gw-test-provider" value={testProvider} onChange={e => setTestProvider(e.target.value)}>
                <option value="openai">OpenAI (External)</option>
                <option value="gemini">Gemini (External)</option>
                <option value="anthropic">Anthropic (External)</option>
                <option value="ollama">Ollama (Local)</option>
              </select>
              <button data-testid="gw-test-run" className="admin-btn-sm accent" onClick={runTestScan} disabled={loading || !((testText) == null ? '' : String(testText)).trim()}>
                {loading ? <RefreshCw size={13} className="spin" /> : <><Eye size={13} /> Scan Uitvoeren</>}
              </button>
            </div>
          </div>

          {testResult && (
            <div className="gw-test-results" data-testid="gw-test-results">
              {/* Summary */}
              <div className="gw-test-summary">
                <div className="gw-test-summary-item">
                  <span>Sensitivity</span>
                  <span className="gw-test-sens-badge" style={{ background: severityColor(testResult.sensitivity), color: '#FFF' }}>
                    {testResult.sensitivity}
                  </span>
                </div>
                <div className="gw-test-summary-item">
                  <span>Model Tier</span>
                  <span className={`gw-tier-badge ${testResult.model_tier}`}>{testResult.model_tier}</span>
                </div>
                <div className="gw-test-summary-item">
                  <span>Downgraded</span>
                  <span>{testResult.tier_downgraded ? 'Ja' : 'Nee'}</span>
                </div>
                <div className="gw-test-summary-item">
                  <span>Detecties</span>
                  <span><strong>{testResult.detections?.length || 0}</strong></span>
                </div>
              </div>

              {/* Detections */}
              {testResult.detections?.length > 0 && (
                <div className="gw-test-detections">
                  <h5>Detecties</h5>
                  {(Array.isArray(testResult.detections) ? testResult.detections : []).map((d, i) => (
                    <div key={i} className="gw-detection-row" data-testid={`gw-detection-${i}`}>
                      <span className="gw-det-type">{d.label}</span>
                      <span className="gw-det-preview">{d.value_preview}</span>
                      <span className="gw-det-action" style={{ background: severityColor(d.sensitivity) + '22', color: severityColor(d.sensitivity) }}>{d.action}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Sanitized Output */}
              <div className="gw-test-output">
                <h5>Gesanitiseerde Output</h5>
                <pre className="gw-test-pre" data-testid="gw-test-sanitized">{testResult.sanitized_text}</pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
